import {ArrowDownIcon, ArrowUpIcon, ChevronDownIcon, ChevronRightIcon, CompassIcon, Loader2Icon, RefreshCwIcon} from "lucide-react";
import React, {type FormEvent, useEffect, useMemo, useRef, useState} from "react";
import {toast} from "sonner";
import {useSWRConfig} from "swr";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {Card, CardContent} from "@components/ui/card";
import {Checkbox} from "@components/ui/checkbox";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@components/ui/collapsible";
import {Input} from "@components/ui/input";
import {Label} from "@components/ui/label";
import {Popover, PopoverContent, PopoverTrigger} from "@components/ui/popover";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@components/ui/select";
import {Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle} from "@components/ui/sheet";
import {Skeleton} from "@components/ui/skeleton";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@components/ui/table";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@components/ui/tabs";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@components/ui/tooltip";
import {
    archiveRevenueTemplate,
    archiveCostTemplate,
    commitVNextModelingWorkbenchBottomUpOverride,
    commitVNextModelingWorkbenchEdit,
    createCostTemplate,
    createRevenueTemplate,
    CostAssumption,
    CostAccountGroupRow,
    CostAccountRow,
    CostBottomUpData,
    CostForecastTemplate,
    CostParentGroupRow,
    CostResidualRow,
    CostScopeType,
    CostSupplierRow,
    costBulkApply,
    CustomerRevenueAssumption,
    deleteCostAssumption,
    deleteCustomerAssumption,
    ExpandReconciliationEntry,
    fetchVNextModelingWorkbenchExpand,
    RevenueForecastTemplate,
    RevenueDriverType,
    RevenueScopeType,
    patchScenario,
    updateRevenueTemplate,
    useCustomerAssumptions,
    useRevenueTemplates,
    ModelingWorkbenchCellValue,
    ModelingWorkbenchData,
    ModelingWorkbenchKpi,
    ModelingWorkbenchKpiPeriods,
    ModelingWorkbenchOpHistoryItem,
    ModelingWorkbenchRow,
    ModelingWorkbenchStatement,
    RevenueAccountRow,
    RevenueBottomUpData,
    RevenueCustomerRow,
    RevenueParentRow,
    RevenueSegmentRow,
    revenueBulkApply,
    saveCostAssumption,
    saveCustomerAssumption,
    useCostAssumptions,
    useCostBottomUp,
    useCostTemplates,
    useRevenueBottomUp,
    updateCostAssumption,
    updateCostTemplate,
    updateCustomerAssumption,
    revertVNextModelingWorkbenchOps,
    useVNextModelingWorkbench,
    useVNextModelingWorkbenchCellDetail,
    VNextOpInput,
    WorkbenchBottomUpOverrideScope,
} from "@hooks/use-vnext";
import {cn} from "@lib/utils";

import {ModelingWorkbenchDetailedTour} from "./ModelingWorkbenchDetailedTour";
import {StickyHorizontalScrollFrame} from "./StickyHorizontalScrollFrame";
import {WorkbenchRevenueDrilldownSheet} from "./WorkbenchRevenueDrilldownSheet";
import {WorkbenchRevenueTieOutSection} from "./WorkbenchRevenueTieOutSection";

interface ModelingWorkbenchTabProps {
    workspaceId: string;
    scenarioId: string;
    actualMonths?: number;
    forecastMonths?: number;
}

type StatementKey = "pnl" | "bs" | "cf";

type ExpandedState = Record<StatementKey, Record<string, boolean>>;
type ExpandedRowsState = Record<StatementKey, Record<string, ModelingWorkbenchRow[]>>;
type ExpandedLoadingState = Record<StatementKey, Record<string, boolean>>;
type ExpandedReconciliationState = Record<StatementKey, Record<string, Record<string, ExpandReconciliationEntry>>>;
type BottomUpOverrideMonthMap = Map<string, Set<string>>;

interface SelectedWorkbenchCell {
    statement: StatementKey;
    nodeId: string;
    nodeLabel: string;
    monthKey: string;
    monthLabel: string;
    status: "actual" | "forecast";
    value: number;
    rowType: ModelingWorkbenchRow["row_type"];
    categoryKey?: string;
    inlineLevel?: ModelingWorkbenchRow["inline_level"];
    isExpandable?: boolean;
    segment?: string;
    parentGroup?: string;
    accountGroup?: string;
    parentContactId?: string;
    contactId?: string;
    sourceAccountId?: string;
    xeroAccountId?: string;
}

function isPnlActualDrilldownCell(cell: SelectedWorkbenchCell | null): boolean {
    return Boolean(
        cell
        && cell.statement === "pnl"
        && cell.status === "actual"
        && (cell.sourceAccountId || (cell.rowType === "account" && !cell.nodeId.includes(":")))
        && !cell.nodeId.startsWith("__"),
    );
}

interface RowVisibilityOptions {
    hideZeroRows: boolean;
    showEditedRowsOnly: boolean;
}

const EMPTY_EXPANDED: ExpandedState = {pnl: {}, bs: {}, cf: {}};
const EMPTY_CHILDREN: ExpandedRowsState = {pnl: {}, bs: {}, cf: {}};
const EMPTY_LOADING: ExpandedLoadingState = {pnl: {}, bs: {}, cf: {}};
const EMPTY_RECONCILIATION: ExpandedReconciliationState = {pnl: {}, bs: {}, cf: {}};
const EMPTY_SEGMENT_ROWS: RevenueSegmentRow[] = [];
const EMPTY_COST_PARENT_GROUPS: CostParentGroupRow[] = [];
const EMPTY_MONTH_KEYS: string[] = [];
const BASELINE_METHOD_OPTIONS = [
    {value: "avg_3m", label: "3-Month Avg"},
    {value: "avg_6m", label: "6-Month Avg"},
    {value: "avg_12m", label: "12-Month Avg"},
    {value: "last_month", label: "Last Month"},
    {value: "yoy_flat", label: "YoY Flat"},
    {value: "yoy_growth", label: "YoY Growth"},
] as const;
const FORECAST_HORIZON_OPTIONS = [6, 12, 18, 24] as const;

function baselineMethodLabel(method: string): string {
    return BASELINE_METHOD_OPTIONS.find((option) => option.value === method)?.label ?? method.replace(/_/g, " ");
}

function baselineMethodFormula(method: string): string {
    const label = baselineMethodLabel(method);
    if (method === "avg_3m") return `${label} (average of the last 3 actual months)`;
    if (method === "avg_6m") return `${label} (average of the last 6 actual months)`;
    if (method === "avg_12m") return `${label} (average of the last 12 actual months)`;
    if (method === "last_month") return `${label} (last actual month repeated)`;
    if (method === "yoy_flat") return `${label} (same month last year)`;
    if (method === "yoy_growth") return `${label} (same month last year with growth)`;
    return `${label} (selected baseline method)`;
}

function formatMonthCount(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return "—";
    const rounded = Math.max(0, Math.round(value));
    return `${rounded} month${rounded === 1 ? "" : "s"}`;
}
function formatCompactCurrency(value: number): string {
    if (Math.abs(value) < 0.5) return "$0";
    const abs = Math.abs(value);
    const sign = value < 0 ? "-" : "";
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
    return `${sign}$${abs.toFixed(0)}`;
}

function formatMetricValue(value: number | null, format: ModelingWorkbenchKpi["format"], compact = false): string {
    if (value == null || !Number.isFinite(value)) return "—";
    if (Math.abs(value) < 0.005 && format !== "months") value = 0;
    if (format === "percent") return `${value.toFixed(1)}%`;
    if (format === "months") return value > 999 ? "Positive" : `${value.toFixed(1)} mo`;
    if (compact) return formatCompactCurrency(value);
    return `$${value.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
}

function formatKpiPeriod(period: string): string {
    const horizonMatch = period.match(/^forecast_horizon_(\d+)$/);
    if (horizonMatch) {
        const count = Number(horizonMatch[1]);
        return `${count}-month forecast`;
    }
    const labels: Record<string, string> = {
        forecast_total: "Forecast total",
        forecast_average: "Forecast average",
        last_forecast: "Last forecast month",
        period_total: "Selected period total",
        period_end: "End of selected period",
        runway: "Runway",
    };
    return labels[period] ?? period.replace(/_/g, " ");
}

type KpiTone = "good" | "warn" | "bad" | "neutral";

function getKpiTone(kpi: ModelingWorkbenchKpi): KpiTone {
    const value = kpi.value ?? null;
    if (value == null || !Number.isFinite(value)) return "neutral";
    if (kpi.key === "balance_check") return Math.abs(value) < 0.5 ? "good" : "bad";
    if (kpi.key === "runway_months") {
        if (value >= 6 || value > 999) return "good";
        if (value >= 3) return "warn";
        return "bad";
    }
    if (["ending_cash", "net_cash_flow", "net_profit"].includes(kpi.key)) {
        if (value > 0) return "good";
        if (value < 0) return "bad";
    }
    if (["gross_margin_pct", "net_profit_pct"].includes(kpi.key)) {
        if (value >= 20) return "good";
        if (value >= 5) return "warn";
        return "bad";
    }
    return "neutral";
}

function getKpiGroup(kpi: ModelingWorkbenchKpi): "Profitability" | "Cash" | "Balance" | "Other" {
    if (["revenue", "cogs", "gross_profit", "gross_margin_pct", "opex", "net_profit", "net_profit_pct"].includes(kpi.key)) return "Profitability";
    if (["ending_cash", "net_cash_flow", "runway_months"].includes(kpi.key)) return "Cash";
    if (["total_assets", "total_liabilities", "net_assets", "balance_check"].includes(kpi.key)) return "Balance";
    return "Other";
}

function getKpiSortIndex(kpi: ModelingWorkbenchKpi): number {
    const order = [
        "revenue",
        "cogs",
        "gross_profit",
        "gross_margin_pct",
        "opex",
        "net_profit",
        "net_profit_pct",
        "ending_cash",
        "net_cash_flow",
        "runway_months",
        "total_assets",
        "total_liabilities",
        "net_assets",
        "balance_check",
    ];
    const index = order.indexOf(kpi.key);
    return index >= 0 ? index : order.length;
}

function formatCellValue(value: number, categoryKey: string): string {
    if (categoryKey.includes("pct") || categoryKey.includes("margin")) {
        return `${value.toFixed(1)}%`;
    }
    return `$${value.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
}

function getForecastStartIndex(months: ModelingWorkbenchStatement["months"]): number {
    const index = months.findIndex((month) => month.status === "forecast");
    return index >= 0 ? index : months.length;
}

function monthStatusByKey(statement: ModelingWorkbenchStatement): Record<string, ModelingWorkbenchCellValue["status"]> {
    return Object.fromEntries(statement.months.map((month) => [month.month_key, month.status]));
}

function totalForMonths(values: Record<string, number>, monthKeys: string[]): number {
    return monthKeys.reduce((sum, monthKey) => sum + Number(values[monthKey] ?? 0), 0);
}

function hasMaterialValue(values: Record<string, number>, monthKeys: string[]): boolean {
    return monthKeys.some((monthKey) => Math.abs(Number(values[monthKey] ?? 0)) >= 0.01);
}

function valuesFromGridMonths(
    values: Record<string, number>,
    statement: ModelingWorkbenchStatement,
    overrideMonthKeys?: Set<string>,
): Record<string, ModelingWorkbenchCellValue> {
    const statusByMonth = monthStatusByKey(statement);
    return Object.fromEntries(
        statement.months.map((month) => [
            month.month_key,
            {
                value: roundCurrency(Number(values[month.month_key] ?? 0)),
                status: statusByMonth[month.month_key] ?? month.status,
                has_ops: month.status === "forecast" && Boolean(overrideMonthKeys?.has(month.month_key)),
                is_override: month.status === "forecast" && Boolean(overrideMonthKeys?.has(month.month_key)),
            },
        ]),
    );
}

function revenueOverrideKey(scopeType: RevenueScopeType, scopeId: string, accountId = ""): string {
    return `revenue:${scopeType}:${scopeId}:${scopeType === "customer" ? accountId : ""}`;
}

function costOverrideKey(
    pnlSection: "cogs" | "expenses",
    scopeType: CostScopeType,
    scopeId: string,
    accountId = "",
): string {
    return `cost:${pnlSection}:${scopeType}:${scopeId}:${scopeType === "supplier" ? accountId : ""}`;
}

// Derive the set of genuinely manual single-cell overrides per scope from the
// ops log, not from assumption.month_overrides. month_overrides also holds bulk
// driver projections (saved from the drilldown grid), so reading it directly
// paints every projected period blue. The ops log only records true exact
// overrides (and their clears), matching the Scenario Assumptions register.
function bottomUpOverrideMonthsFromOps(
    ops: ModelingWorkbenchOpHistoryItem[] | undefined,
    kind: "revenue" | "cost",
): BottomUpOverrideMonthMap {
    const map: BottomUpOverrideMonthMap = new Map();
    for (const op of ops ?? []) {
        if (op.op_type !== "set_override") continue;
        const params = (op.params ?? {}) as Record<string, unknown>;
        if (params.source !== "workbench_bottom_up_override") continue;
        if (params.kind !== kind) continue;
        const month = typeof params.month_key === "string" ? params.month_key : op.target_month;
        if (!month) continue;
        const scopeId = String(params.scope_id ?? "");
        const scopeType = String(params.scope_type ?? "");
        if (!scopeId || !scopeType) continue;

        let key: string;
        if (kind === "revenue") {
            if (scopeType !== "customer") continue;
            key = revenueOverrideKey("customer", scopeId, String(params.account_id ?? ""));
        } else {
            const pnlSection = String(params.pnl_section ?? "");
            if (pnlSection !== "cogs" && pnlSection !== "expenses") continue;
            if (scopeType !== "account" && scopeType !== "supplier") continue;
            const accountId = String(params.account_id ?? "");
            if (scopeType === "supplier" && !accountId) continue;
            key = costOverrideKey(pnlSection, scopeType as CostScopeType, scopeId, accountId);
        }

        let set = map.get(key);
        if (!set) {
            set = new Set<string>();
            map.set(key, set);
        }
        if (params.cleared === true) {
            set.delete(month);
        } else {
            set.add(month);
        }
    }
    for (const [key, set] of map) {
        if (set.size === 0) map.delete(key);
    }
    return map;
}

function roundCurrency(value: number): number {
    return Math.round(value * 100) / 100;
}

function pnlSectionKeyForRow(row: ModelingWorkbenchRow): "revenue" | "cost_of_sales" | "expenses" | "other" | null {
    const key = (row.category_key || row.node_id || "").toLowerCase();
    if (key.startsWith("revenue") || key === "other" || key.includes("other_income")) {
        return key.startsWith("revenue") ? "revenue" : "other";
    }
    if (key.startsWith("cost_of_sales") || key.startsWith("costs") || key.includes("directcost")) {
        return "cost_of_sales";
    }
    if (key.startsWith("expenses") || key.startsWith("opex") || key.includes("expense")) {
        return "expenses";
    }
    return null;
}

function isRevenueRow(row: ModelingWorkbenchRow): boolean {
    return pnlSectionKeyForRow(row) === "revenue";
}

function revenueSourceAccountId(row: ModelingWorkbenchRow): string {
    return row.source_account_id || row.node_id;
}

function revenueAccountForRow(
    revenueGrid: RevenueBottomUpData,
    row: ModelingWorkbenchRow,
): RevenueAccountRow | null {
    const accounts = revenueGrid.accounts;
    if (!accounts || accounts.length === 0) return null;
    const accountId = revenueSourceAccountId(row);
    return (
        accounts.find((candidate) => candidate.account_node_id === accountId)
        ?? (row.xero_account_id
            ? accounts.find((candidate) => candidate.xero_account_id === row.xero_account_id)
            : undefined)
        ?? null
    );
}

function revenueSegmentToWorkbenchRow(
    segment: RevenueSegmentRow,
    sourceRow: ModelingWorkbenchRow,
    statement: ModelingWorkbenchStatement,
    account?: RevenueAccountRow,
): ModelingWorkbenchRow {
    const monthKeys = statement.months.map((month) => month.month_key);
    return {
        node_id: `revenue-segment:${revenueSourceAccountId(sourceRow)}:${segment.label}`,
        label: segment.label,
        category_key: sourceRow.category_key || "revenue",
        depth: sourceRow.depth + 1,
        row_type: "line_item",
        is_expandable: segment.parents.length > 0,
        expand_level: segment.parents.length > 0 ? "customer_parent" : null,
        inline_level: "segment",
        segment: segment.label,
        source_account_id: revenueSourceAccountId(sourceRow),
        xero_account_id: account?.xero_account_id ?? sourceRow.xero_account_id,
        is_bold: false,
        values: valuesFromGridMonths(segment.months, statement),
        total: roundCurrency(totalForMonths(segment.months, monthKeys)),
    };
}

function revenueParentToWorkbenchRow(
    parent: RevenueParentRow,
    segment: RevenueSegmentRow,
    sourceRow: ModelingWorkbenchRow,
    statement: ModelingWorkbenchStatement,
): ModelingWorkbenchRow {
    const monthKeys = statement.months.map((month) => month.month_key);
    return {
        node_id: `revenue-parent:${revenueSourceAccountId(sourceRow)}:${segment.label}:${parent.id}`,
        label: parent.name,
        category_key: sourceRow.category_key || "revenue",
        depth: sourceRow.depth + 1,
        row_type: "line_item",
        is_expandable: parent.children.length > 0,
        expand_level: parent.children.length > 0 ? "customer_child" : null,
        inline_level: "customer_parent",
        segment: segment.label,
        parent_contact_id: parent.id,
        contact_id: parent.id,
        source_account_id: revenueSourceAccountId(sourceRow),
        xero_account_id: sourceRow.xero_account_id,
        is_bold: false,
        values: valuesFromGridMonths(parent.months, statement),
        total: roundCurrency(totalForMonths(parent.months, monthKeys)),
    };
}

function revenueCustomerToWorkbenchRow(
    customer: RevenueCustomerRow,
    parent: RevenueParentRow,
    segment: RevenueSegmentRow,
    sourceRow: ModelingWorkbenchRow,
    statement: ModelingWorkbenchStatement,
    revenueOverrides?: BottomUpOverrideMonthMap,
): ModelingWorkbenchRow {
    const monthKeys = statement.months.map((month) => month.month_key);
    const overrideMonthKeys = revenueOverrides?.get(
        revenueOverrideKey("customer", customer.id, sourceRow.xero_account_id || ""),
    );
    return {
        node_id: `revenue-customer:${revenueSourceAccountId(sourceRow)}:${segment.label}:${parent.id}:${customer.id}`,
        label: customer.name,
        category_key: sourceRow.category_key || "revenue",
        depth: sourceRow.depth + 1,
        row_type: "line_item",
        is_expandable: false,
        expand_level: null,
        inline_level: "customer_child",
        segment: segment.label,
        parent_contact_id: parent.id,
        contact_id: customer.id,
        source_account_id: revenueSourceAccountId(sourceRow),
        xero_account_id: sourceRow.xero_account_id,
        is_bold: false,
        values: valuesFromGridMonths(customer.months, statement, overrideMonthKeys),
        total: roundCurrency(totalForMonths(customer.months, monthKeys)),
    };
}

export function buildRevenueInlineChildren(
    row: ModelingWorkbenchRow,
    revenueGrid: RevenueBottomUpData | null | undefined,
    statement: ModelingWorkbenchStatement,
    revenueOverrides?: BottomUpOverrideMonthMap,
): ModelingWorkbenchRow[] | null {
    if (!revenueGrid) return null;
    const monthKeys = statement.months.map((month) => month.month_key);
    const hasAccountAxis = Boolean(revenueGrid.accounts && revenueGrid.accounts.length > 0);

    // Account-scoped nesting (D1 fix): only the matching account's customer
    // tree renders beneath an account row — never the whole-tenant grid.
    const account = revenueAccountForRow(revenueGrid, row);
    const scopedSegments = account ? account.segments : hasAccountAxis ? null : revenueGrid.segments;

    if (!row.inline_level && row.depth > 0 && isRevenueRow(row)) {
        if (!scopedSegments) return null; // no customer-billed leaves under this account
        return scopedSegments
            .filter((segment) => hasMaterialValue(segment.months, monthKeys) || segment.parents.length > 0)
            .map((segment) => revenueSegmentToWorkbenchRow(segment, row, statement, account ?? undefined));
    }

    if (row.inline_level === "segment") {
        if (!scopedSegments) return null;
        const segment = scopedSegments.find((candidate) => candidate.label === row.segment || candidate.label === row.label);
        if (!segment) return null;
        return segment.parents
            .filter((parent) => hasMaterialValue(parent.months, monthKeys) || parent.children.length > 0)
            .map((parent) => revenueParentToWorkbenchRow(parent, segment, row, statement));
    }

    if (row.inline_level === "customer_parent") {
        if (!scopedSegments) return null;
        const segment = scopedSegments.find((candidate) => candidate.label === row.segment);
        const parent = segment?.parents.find((candidate) => candidate.id === row.parent_contact_id || candidate.id === row.contact_id);
        if (!segment || !parent) return null;
        return parent.children
            .filter((customer) => hasMaterialValue(customer.months, monthKeys))
            .map((customer) => revenueCustomerToWorkbenchRow(customer, parent, segment, row, statement, revenueOverrides));
    }

    return null;
}

function costGridSectionForRow(row: ModelingWorkbenchRow): "cogs" | "expenses" | null {
    const sectionKey = pnlSectionKeyForRow(row);
    if (sectionKey === "cost_of_sales") return "cogs";
    if (sectionKey === "expenses") return "expenses";
    return null;
}

function costParentGroupsForSection(
    costGrid: CostBottomUpData | null | undefined,
    section: "cogs" | "expenses" | null,
): CostParentGroupRow[] {
    if (!costGrid || !section) return [];
    return costGrid.parent_groups.filter((group) => {
        const groupSection = String(group.pnl_section || "").toLowerCase();
        return section === "cogs"
            ? groupSection === "cogs" || groupSection === "cost_of_sales"
            : groupSection === "expenses";
    });
}

function costResidualHasValue(residual: CostResidualRow | null | undefined, monthKeys: string[]): boolean {
    return Boolean(residual && hasMaterialValue(residual.months, monthKeys));
}

function costResidualToWorkbenchRow({
    residual,
    label,
    nodeId,
    sourceRow,
    statement,
    depth,
    overrideMonthKeys,
}: {
    residual: CostResidualRow;
    label: string;
    nodeId: string;
    sourceRow: ModelingWorkbenchRow;
    statement: ModelingWorkbenchStatement;
    depth: number;
    overrideMonthKeys?: Set<string>;
}): ModelingWorkbenchRow {
    const monthKeys = statement.months.map((month) => month.month_key);
    return {
        node_id: nodeId,
        label,
        category_key: sourceRow.category_key,
        depth,
        row_type: "line_item",
        is_expandable: false,
        expand_level: null,
        inline_level: "supplier",
        parent_group: sourceRow.parent_group,
        account_group: sourceRow.account_group,
        source_account_id: sourceRow.source_account_id,
        xero_account_id: sourceRow.xero_account_id,
        is_bold: false,
        values: valuesFromGridMonths(residual.months, statement, overrideMonthKeys),
        total: roundCurrency(totalForMonths(residual.months, monthKeys)),
        assumption_summary: "Residual row keeps the cost hierarchy tied to the parent total.",
    };
}

function costParentGroupToWorkbenchRow(
    parentGroup: CostParentGroupRow,
    sourceRow: ModelingWorkbenchRow,
    statement: ModelingWorkbenchStatement,
): ModelingWorkbenchRow {
    const monthKeys = statement.months.map((month) => month.month_key);
    const hasChildren = parentGroup.account_groups.length > 0 || costResidualHasValue(parentGroup.residual, monthKeys);
    return {
        node_id: `cost-parent-group:${parentGroup.pnl_section}:${parentGroup.label}`,
        label: parentGroup.label,
        category_key: sourceRow.category_key,
        depth: sourceRow.depth + 1,
        row_type: "line_item",
        is_expandable: hasChildren,
        expand_level: hasChildren ? "account_group" : null,
        inline_level: "parent_group",
        parent_group: parentGroup.label,
        is_bold: false,
        values: valuesFromGridMonths(parentGroup.months, statement),
        total: roundCurrency(totalForMonths(parentGroup.months, monthKeys)),
    };
}

function costAccountGroupToWorkbenchRow(
    accountGroup: CostAccountGroupRow,
    parentGroup: CostParentGroupRow,
    sourceRow: ModelingWorkbenchRow,
    statement: ModelingWorkbenchStatement,
): ModelingWorkbenchRow {
    const monthKeys = statement.months.map((month) => month.month_key);
    const hasChildren = accountGroup.accounts.length > 0 || costResidualHasValue(accountGroup.residual, monthKeys);
    return {
        node_id: `cost-account-group:${parentGroup.pnl_section}:${parentGroup.label}:${accountGroup.label}`,
        label: accountGroup.label,
        category_key: sourceRow.category_key,
        depth: sourceRow.depth + 1,
        row_type: "line_item",
        is_expandable: hasChildren,
        expand_level: hasChildren ? "account" : null,
        inline_level: "account_group",
        parent_group: parentGroup.label,
        account_group: accountGroup.label,
        is_bold: false,
        values: valuesFromGridMonths(accountGroup.months, statement),
        total: roundCurrency(totalForMonths(accountGroup.months, monthKeys)),
    };
}

function costAccountToWorkbenchRow(
    account: CostAccountRow,
    accountGroup: CostAccountGroupRow,
    parentGroup: CostParentGroupRow,
    sourceRow: ModelingWorkbenchRow,
    statement: ModelingWorkbenchStatement,
    costOverrides?: BottomUpOverrideMonthMap,
): ModelingWorkbenchRow {
    const monthKeys = statement.months.map((month) => month.month_key);
    const sourceAccountId = account.node_id || sourceRow.source_account_id;
    const hasChildren = account.suppliers.length > 0 || costResidualHasValue(account.residual, monthKeys);
    const pnlSection = parentGroup.pnl_section === "cogs" ? "cogs" : "expenses";
    const overrideMonthKeys = !hasChildren
        ? costOverrides?.get(costOverrideKey(pnlSection, "account", account.id))
        : undefined;
    return {
        node_id: sourceAccountId || `cost-account:${parentGroup.pnl_section}:${account.id}`,
        label: account.code ? `${account.code} · ${account.name}` : account.name,
        category_key: sourceRow.category_key,
        depth: sourceRow.depth + 1,
        row_type: sourceAccountId ? "account" : "line_item",
        is_expandable: hasChildren,
        expand_level: hasChildren ? "supplier" : null,
        inline_level: "account",
        parent_group: parentGroup.label,
        account_group: accountGroup.label,
        source_account_id: sourceAccountId,
        xero_account_id: account.id,
        is_bold: false,
        values: valuesFromGridMonths(account.months, statement, overrideMonthKeys),
        total: roundCurrency(totalForMonths(account.months, monthKeys)),
        assumption_summary: account.has_assumption ? `Cost assumption: ${account.driver_type || "custom"}` : null,
    };
}

function costSupplierToWorkbenchRow(
    supplier: CostSupplierRow,
    account: CostAccountRow,
    accountGroup: CostAccountGroupRow,
    parentGroup: CostParentGroupRow,
    sourceRow: ModelingWorkbenchRow,
    statement: ModelingWorkbenchStatement,
    costOverrides?: BottomUpOverrideMonthMap,
): ModelingWorkbenchRow {
    const monthKeys = statement.months.map((month) => month.month_key);
    const sourceAccountId = account.node_id || sourceRow.source_account_id;
    const pnlSection = parentGroup.pnl_section === "cogs" ? "cogs" : "expenses";
    const overrideMonthKeys = supplier.id === "_no_supplier"
        ? undefined
        : costOverrides?.get(costOverrideKey(pnlSection, "supplier", supplier.id, account.id));
    return {
        node_id: `cost-supplier:${parentGroup.pnl_section}:${account.id}:${supplier.id}`,
        label: supplier.name,
        category_key: sourceRow.category_key,
        depth: sourceRow.depth + 1,
        row_type: "line_item",
        is_expandable: false,
        expand_level: null,
        inline_level: "supplier",
        parent_group: parentGroup.label,
        account_group: accountGroup.label,
        contact_id: supplier.id === "_no_supplier" ? undefined : supplier.id,
        source_account_id: sourceAccountId,
        xero_account_id: account.id,
        is_bold: false,
        values: valuesFromGridMonths(supplier.months, statement, overrideMonthKeys),
        total: roundCurrency(totalForMonths(supplier.months, monthKeys)),
        assumption_summary: supplier.has_assumption ? `Supplier assumption: ${supplier.driver_type || "custom"}` : null,
    };
}

function findCostParentGroup(
    costGrid: CostBottomUpData | null | undefined,
    row: ModelingWorkbenchRow,
): CostParentGroupRow | null {
    const section = costGridSectionForRow(row);
    return costParentGroupsForSection(costGrid, section).find((group) => group.label === row.parent_group || group.label === row.label) ?? null;
}

function findCostAccountGroup(
    parentGroup: CostParentGroupRow | null,
    row: ModelingWorkbenchRow,
): CostAccountGroupRow | null {
    if (!parentGroup) return null;
    return parentGroup.account_groups.find((group) => group.label === row.account_group || group.label === row.label) ?? null;
}

function findCostAccount(
    accountGroup: CostAccountGroupRow | null,
    row: ModelingWorkbenchRow,
): CostAccountRow | null {
    if (!accountGroup) return null;
    return accountGroup.accounts.find((account) => {
        return account.id === row.xero_account_id || account.node_id === row.source_account_id || account.name === row.label;
    }) ?? null;
}

function buildCostInlineChildren(
    row: ModelingWorkbenchRow,
    costGrid: CostBottomUpData | null | undefined,
    statement: ModelingWorkbenchStatement,
    costOverrides?: BottomUpOverrideMonthMap,
): ModelingWorkbenchRow[] | null {
    const monthKeys = statement.months.map((month) => month.month_key);
    const section = costGridSectionForRow(row);
    if (!costGrid || !section) return null;

    if (row.depth === 0) {
        return costParentGroupsForSection(costGrid, section)
            .filter((group) => hasMaterialValue(group.months, monthKeys) || group.account_groups.length > 0)
            .map((group) => costParentGroupToWorkbenchRow(group, row, statement));
    }

    if (row.inline_level === "parent_group") {
        const parentGroup = findCostParentGroup(costGrid, row);
        if (!parentGroup) return null;
        const rows = parentGroup.account_groups
            .filter((group) => hasMaterialValue(group.months, monthKeys) || group.accounts.length > 0)
            .map((group) => costAccountGroupToWorkbenchRow(group, parentGroup, row, statement));
        if (costResidualHasValue(parentGroup.residual, monthKeys)) {
            rows.push(costResidualToWorkbenchRow({
                residual: parentGroup.residual as CostResidualRow,
                label: "Other / Parent Residual",
                nodeId: `cost-parent-residual:${parentGroup.pnl_section}:${parentGroup.label}`,
                sourceRow: row,
                statement,
                depth: row.depth + 1,
            }));
        }
        return rows;
    }

    if (row.inline_level === "account_group") {
        const parentGroup = findCostParentGroup(costGrid, row);
        const accountGroup = findCostAccountGroup(parentGroup, row);
        if (!parentGroup || !accountGroup) return null;
        const rows = accountGroup.accounts
            .filter((account) => hasMaterialValue(account.months, monthKeys) || account.suppliers.length > 0)
            .map((account) => costAccountToWorkbenchRow(account, accountGroup, parentGroup, row, statement, costOverrides));
        if (costResidualHasValue(accountGroup.residual, monthKeys)) {
            rows.push(costResidualToWorkbenchRow({
                residual: accountGroup.residual as CostResidualRow,
                label: "Other / Account Group Residual",
                nodeId: `cost-account-group-residual:${parentGroup.pnl_section}:${parentGroup.label}:${accountGroup.label}`,
                sourceRow: row,
                statement,
                depth: row.depth + 1,
            }));
        }
        return rows;
    }

    if (row.inline_level === "account") {
        const parentGroup = findCostParentGroup(costGrid, row);
        const accountGroup = findCostAccountGroup(parentGroup, row);
        const account = findCostAccount(accountGroup, row);
        if (!parentGroup || !accountGroup || !account) return null;
        const rows = account.suppliers
            .filter((supplier) => hasMaterialValue(supplier.months, monthKeys))
            .map((supplier) => costSupplierToWorkbenchRow(supplier, account, accountGroup, parentGroup, row, statement, costOverrides));
        if (costResidualHasValue(account.residual, monthKeys)) {
            const pnlSection = parentGroup.pnl_section === "cogs" ? "cogs" : "expenses";
            rows.push(costResidualToWorkbenchRow({
                residual: account.residual as CostResidualRow,
                label: "Other / Account Residual",
                nodeId: `cost-account-residual:${parentGroup.pnl_section}:${account.id}`,
                sourceRow: row,
                statement,
                depth: row.depth + 1,
                overrideMonthKeys: costOverrides?.get(costOverrideKey(pnlSection, "account", account.id)),
            }));
        }
        return rows;
    }

    return null;
}

interface PnlWorkbenchExpandRequest {
    sectionKey: string;
    options?: {
        level?: string;
        parentGroup?: string;
        accountGroup?: string;
        accountId?: string;
        accountNodeId?: string;
        segment?: string;
        parentContactId?: string;
    };
}

function getPnlWorkbenchExpandRequest(row: ModelingWorkbenchRow): PnlWorkbenchExpandRequest | null {
    const sectionKey = pnlSectionKeyForRow(row);
    if (!sectionKey) return null;

    if (row.depth === 0) {
        return {sectionKey: row.node_id};
    }

    if (row.inline_level === "parent_group") {
        return {sectionKey, options: {level: "account_group", parentGroup: row.parent_group || row.label}};
    }
    if (row.inline_level === "account_group") {
        return {
            sectionKey,
            options: {
                level: "account",
                parentGroup: row.parent_group,
                accountGroup: row.account_group || row.label,
            },
        };
    }
    if (row.inline_level === "account" || row.row_type === "account") {
        return {
            sectionKey,
            options: {
                level: sectionKey === "revenue" || sectionKey === "other" ? "segment" : "supplier",
                accountId: row.xero_account_id || row.source_account_id,
                accountNodeId: row.source_account_id || row.node_id,
            },
        };
    }
    if (row.inline_level === "segment") {
        return {sectionKey, options: {level: "customer_parent", segment: row.segment || row.label}};
    }
    if (row.inline_level === "customer_parent") {
        return {
            sectionKey,
            options: {
                level: "customer_child",
                segment: row.segment,
                parentContactId: row.parent_contact_id || row.contact_id,
                accountNodeId: row.source_account_id,
            },
        };
    }

    return null;
}

function flattenStatementRows(
    rows: ModelingWorkbenchRow[],
    expanded: Record<string, boolean>,
    children: Record<string, ModelingWorkbenchRow[]>,
    visibility: RowVisibilityOptions,
): ModelingWorkbenchRow[] {
    const flat: ModelingWorkbenchRow[] = [];
    const appendRow = (row: ModelingWorkbenchRow) => {
        if (!shouldDisplayRow(row, visibility)) return;
        flat.push(row);
        if (expanded[row.node_id] && children[row.node_id]?.length) {
            children[row.node_id].forEach(appendRow);
        }
    };
    rows.forEach(appendRow);
    return flat;
}

function isZeroRow(row: ModelingWorkbenchRow): boolean {
    if (Math.abs(row.total) >= 0.01) return false;
    return Object.values(row.values).every((cell) => Math.abs(cell.value) < 0.01);
}

function isEditedRow(row: ModelingWorkbenchRow): boolean {
    return Object.values(row.values).some((cell) => cell.has_ops || cell.is_override);
}

function shouldDisplayRow(row: ModelingWorkbenchRow, visibility: RowVisibilityOptions): boolean {
    if (row.row_type === "variance") return false;
    if (visibility.hideZeroRows && isZeroRow(row)) return false;
    if (visibility.showEditedRowsOnly && row.depth > 0 && !isEditedRow(row)) return false;
    return true;
}

function buildWorkbenchNodeLabelMap(
    statements: ModelingWorkbenchData["statements"] | null | undefined,
    expandedRows: ExpandedRowsState,
): Map<string, string> {
    const labels = new Map<string, string>();
    const rememberRows = (rows: ModelingWorkbenchRow[]) => {
        rows.forEach((row) => {
            if (row.node_id && row.label) {
                labels.set(row.node_id, row.label);
            }
        });
    };

    if (statements) {
        rememberRows(statements.pnl.rows);
        rememberRows(statements.bs.rows);
        rememberRows(statements.cf.rows);
    }

    (["pnl", "bs", "cf"] as const).forEach((statementKey) => {
        Object.values(expandedRows[statementKey]).forEach((rows) => rememberRows(rows));
    });

    return labels;
}

function getCellHoverSummary(row: ModelingWorkbenchRow, cell: ModelingWorkbenchRow["values"][string], monthLabel: string): string {
    if (cell.status === "actual") {
        return `${monthLabel} actual. Click for source detail and transaction context.`;
    }
    if (cell.is_override) {
        return `${monthLabel} forecast override. Click for assumptions, revisions, and editing.`;
    }
    if (cell.has_ops) {
        return `${monthLabel} forecast with active scenario ops. Click for detail.`;
    }
    if (row.assumption_summary) return row.assumption_summary;
    return `${monthLabel} forecast cell. Click for assumptions, dependencies, and revisions.`;
}

function parseOverrideValue(value: string): number {
    return Number(value.replace(/[$,\s]/g, ""));
}

function KpiHealthBadge({kpi}: {kpi: ModelingWorkbenchKpi}) {
    const tone = getKpiTone(kpi);
    const label =
        kpi.key === "balance_check"
            ? tone === "good" ? "Balanced" : "Review"
            : tone === "good" ? "Healthy" : tone === "warn" ? "Watch" : tone === "bad" ? "Pressure" : "Monitor";
    return (
        <span
            className={cn(
                "rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.8px]",
                tone === "good" && "bg-emerald-50 text-emerald-700",
                tone === "warn" && "bg-amber-50 text-amber-700",
                tone === "bad" && "bg-rose-50 text-rose-700",
                tone === "neutral" && "bg-slate-100 text-slate-600",
            )}
        >
            {label}
        </span>
    );
}

const UP_IS_BAD_KPI_KEYS = new Set(["cogs", "opex"]);

function VsBaseIndicator({kpi, hasScenarioOps}: {kpi: ModelingWorkbenchKpi; hasScenarioOps: boolean}) {
    if (!hasScenarioOps) return null;
    if (kpi.baseline == null || kpi.value == null) return null;
    const diff = kpi.value - kpi.baseline;
    if (Math.abs(diff) < 0.05) return null;

    const pct = kpi.baseline !== 0 ? ((diff / Math.abs(kpi.baseline)) * 100) : null;
    const isUp = diff > 0;
    const inverted = UP_IS_BAD_KPI_KEYS.has(kpi.key);
    const isGood = inverted ? !isUp : isUp;
    const color = isGood ? "text-emerald-600" : "text-rose-600";

    const formatted = kpi.format === "percent"
        ? `${diff > 0 ? "+" : ""}${diff.toFixed(1)}pp`
        : `${diff > 0 ? "+" : ""}${formatCompactCurrency(diff)}`;

    return (
        <div className={`mt-1 flex items-center gap-0.5 ${color}`}>
            {isUp ? <ArrowUpIcon className="size-2.5" /> : <ArrowDownIcon className="size-2.5" />}
            <span className="text-[10px] font-medium">
                {formatted}
                {pct != null && ` (${pct > 0 ? "+" : ""}${pct.toFixed(0)}%)`}
            </span>
            <span className="text-[10px] text-slate-400 ml-0.5">vs base</span>
        </div>
    );
}

function TopKpiStrip({
    kpis,
    kpiPeriods,
    hasScenarioOps,
}: {
    kpis: ModelingWorkbenchKpi[];
    kpiPeriods?: ModelingWorkbenchKpiPeriods;
    hasScenarioOps: boolean;
}) {
    const [periodKey, setPeriodKey] = useState("forecast");
    const periodOptions = kpiPeriods?.options ?? [];
    const activeKpis = kpiPeriods?.kpis_by_period?.[periodKey] ?? kpis;
    const grouped = useMemo(() => {
        const buckets: Record<ReturnType<typeof getKpiGroup>, ModelingWorkbenchKpi[]> = {
            Profitability: [],
            Cash: [],
            Balance: [],
            Other: [],
        };
        [...activeKpis].sort((a, b) => getKpiSortIndex(a) - getKpiSortIndex(b)).forEach((kpi) => {
            buckets[getKpiGroup(kpi)].push(kpi);
        });
        return buckets;
    }, [activeKpis]);
    const sections = (["Profitability", "Cash", "Balance", "Other"] as const).filter((section) => grouped[section].length > 0);

    return (
        <section className="rounded-2xl border border-[#E2E8F0] bg-white p-3 shadow-sm" data-tour="workbench-revenue-planning">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[1.8px] text-sky-700">Workbench KPIs</p>
                    <h2 className="mt-0.5 text-sm font-semibold text-slate-900">Forecast control panel</h2>
                    <p className="mt-0.5 text-xs text-slate-500">Selected period totals plus end-of-period liquidity and balance checks.</p>
                </div>
                <div className="flex items-center gap-2">
                    {periodOptions.length > 1 && (
                        <Select value={periodKey} onValueChange={setPeriodKey}>
                            <SelectTrigger className="h-8 w-[160px] bg-white text-xs" aria-label="KPI period">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {periodOptions.map((option) => (
                                    <SelectItem key={option.key} value={option.key} className="text-xs">
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    <Badge className="bg-sky-50 text-xs text-sky-700 hover:bg-sky-50">Modeling Preview</Badge>
                </div>
            </div>
            <div className="mt-3 grid gap-2 xl:grid-cols-3">
                {sections.map((section) => (
                    <div key={section} className="rounded-xl border border-slate-200 bg-slate-50/60 p-2.5">
                        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[1.8px] text-slate-500">{section}</p>
                        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                            {grouped[section].map((kpi) => {
                                const tone = getKpiTone(kpi);
                                return (
                                    <Card
                                        key={kpi.key}
                                        className={cn(
                                            "border bg-white shadow-none",
                                            tone === "good" && "border-emerald-100",
                                            tone === "warn" && "border-amber-100",
                                            tone === "bad" && "border-rose-100",
                                            tone === "neutral" && "border-slate-200",
                                        )}
                                    >
                                        <CardContent className="p-2.5">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
                                                <KpiHealthBadge kpi={kpi} />
                                            </div>
                                            <p className="mt-1 text-base font-semibold leading-tight text-slate-950">{formatMetricValue(kpi.value, kpi.format, true)}</p>
                                            <VsBaseIndicator kpi={kpi} hasScenarioOps={hasScenarioOps} />
                                            <p className="mt-0.5 text-[10px] text-slate-500">{formatKpiPeriod(kpi.period)}</p>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}


function RowVisibilityControls({
    options,
    onChange,
}: {
    options: RowVisibilityOptions;
    onChange: (next: RowVisibilityOptions) => void;
}) {
    return (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-none" data-tour="workbench-view-controls">
            <div className="flex flex-wrap items-center gap-6">
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-500">View Controls</p>
                    <p className="mt-1 text-sm text-slate-500">Keep the model readable while drilling into linked statements.</p>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                    <Label className="cursor-pointer text-sm text-slate-700">
                        <Checkbox
                            checked={options.hideZeroRows}
                            onCheckedChange={(checked) => onChange({...options, hideZeroRows: checked === true})}
                            aria-label="Hide zero rows"
                        />
                        Hide zero rows
                    </Label>
                    <Label className="cursor-pointer text-sm text-slate-700">
                        <Checkbox
                            checked={options.showEditedRowsOnly}
                            onCheckedChange={(checked) => onChange({...options, showEditedRowsOnly: checked === true})}
                            aria-label="Show edited rows only"
                        />
                        Show edited rows only
                    </Label>
                </div>
            </div>
        </section>
    );
}

function sumMonthValues(values: Record<string, number>, monthKeys: string[]): number {
    return monthKeys.reduce((total, monthKey) => total + (values[monthKey] ?? 0), 0);
}

type RevenueScopeOption = {
    key: string;
    scopeType: RevenueScopeType;
    scopeId: string;
    scopeLabel: string;
    description: string;
};

type CostScopeOption = {
    key: string;
    pnlSection: "cogs" | "expenses";
    scopeType: CostScopeType;
    scopeId: string;
    scopeLabel: string;
    description: string;
    accountId?: string;
};

type RevenueAssumptionMode = "direct" | "template";
type CostAssumptionMode = "direct" | "template";

function costAssumptionMapKey(
    pnlSection: "cogs" | "expenses",
    scopeType: CostScopeType,
    scopeId: string,
    accountId = "",
): string {
    return `${pnlSection}:${scopeType}:${scopeId}:${scopeType === "supplier" ? accountId : ""}`;
}

function costScopeOptionKey(
    pnlSection: "cogs" | "expenses",
    scopeType: CostScopeType,
    scopeId: string,
    accountId = "",
): string {
    if (scopeType === "supplier") return `${pnlSection}:supplier:${accountId}:${scopeId}`;
    return `${pnlSection}:${scopeType}:${scopeId}`;
}

type ForecastAssumptionScope =
    | {
        kind: "revenue";
        scopeType: RevenueScopeType;
        scopeId: string;
        scopeLabel: string;
        accountId?: string;
        canBulkApply: boolean;
        bulkScopeType?: "segment" | "parent";
    }
    | {
        kind: "cost";
        pnlSection: "cogs" | "expenses";
        scopeType: CostScopeType;
        scopeId: string;
        scopeLabel: string;
        accountId?: string;
        canBulkApply: boolean;
    };

function isRevenueCell(cell: SelectedWorkbenchCell): boolean {
    return pnlSectionKeyForRow({
        node_id: cell.nodeId,
        label: cell.nodeLabel,
        category_key: cell.categoryKey || "",
        depth: 0,
        row_type: cell.rowType,
        is_expandable: false,
        expand_level: null,
        is_bold: false,
        values: {},
        total: 0,
    }) === "revenue";
}

function forecastAssumptionScopeForCell(cell: SelectedWorkbenchCell | null): ForecastAssumptionScope | null {
    if (!cell || cell.statement !== "pnl" || cell.status !== "forecast" || !cell.inlineLevel) return null;

    if (isRevenueCell(cell)) {
        if (cell.inlineLevel === "segment") {
            return {
                kind: "revenue",
                scopeType: "segment",
                scopeId: cell.segment || cell.nodeLabel,
                scopeLabel: cell.nodeLabel,
                canBulkApply: true,
                bulkScopeType: "segment",
            };
        }
        if (cell.inlineLevel === "customer_parent") {
            const parentId = cell.parentContactId || cell.contactId;
            if (!parentId) return null;
            return {
                kind: "revenue",
                scopeType: "parent",
                scopeId: parentId,
                scopeLabel: cell.nodeLabel,
                canBulkApply: true,
                bulkScopeType: "parent",
            };
        }
        if (cell.inlineLevel === "customer_child" && cell.contactId) {
            return {
                kind: "revenue",
                scopeType: "customer",
                scopeId: cell.contactId,
                scopeLabel: cell.nodeLabel,
                accountId: cell.xeroAccountId || undefined,
                canBulkApply: false,
            };
        }
        return null;
    }

    const section = pnlSectionKeyForRow({
        node_id: cell.nodeId,
        label: cell.nodeLabel,
        category_key: cell.categoryKey || "",
        depth: 0,
        row_type: cell.rowType,
        is_expandable: false,
        expand_level: null,
        is_bold: false,
        values: {},
        total: 0,
    });
    const pnlSection = section === "cost_of_sales" ? "cogs" : section === "expenses" ? "expenses" : null;
    if (!pnlSection) return null;

    if (cell.inlineLevel === "parent_group") {
        return {
            kind: "cost",
            pnlSection,
            scopeType: "parent_group",
            scopeId: cell.parentGroup || cell.nodeLabel,
            scopeLabel: cell.nodeLabel,
            canBulkApply: true,
        };
    }
    if (cell.inlineLevel === "account_group") {
        return {
            kind: "cost",
            pnlSection,
            scopeType: "account_group",
            scopeId: cell.accountGroup || cell.nodeLabel,
            scopeLabel: cell.nodeLabel,
            canBulkApply: true,
        };
    }
    if (cell.inlineLevel === "account") {
        const accountId = cell.xeroAccountId || cell.sourceAccountId;
        if (!accountId) return null;
        return {
            kind: "cost",
            pnlSection,
            scopeType: "account",
            scopeId: accountId,
            scopeLabel: cell.nodeLabel,
            accountId,
            canBulkApply: true,
        };
    }
    if (cell.inlineLevel === "supplier") {
        if (cell.contactId && cell.xeroAccountId) {
            return {
                kind: "cost",
                pnlSection,
                scopeType: "supplier",
                scopeId: cell.contactId,
                scopeLabel: cell.nodeLabel,
                accountId: cell.xeroAccountId,
                canBulkApply: false,
            };
        }
        if (cell.xeroAccountId) {
            return {
                kind: "cost",
                pnlSection,
                scopeType: "account",
                scopeId: cell.xeroAccountId,
                scopeLabel: cell.nodeLabel,
                accountId: cell.xeroAccountId,
                canBulkApply: true,
            };
        }
        if (cell.accountGroup) {
            return {
                kind: "cost",
                pnlSection,
                scopeType: "account_group",
                scopeId: cell.accountGroup,
                scopeLabel: cell.accountGroup,
                canBulkApply: true,
            };
        }
        if (cell.parentGroup) {
            return {
                kind: "cost",
                pnlSection,
                scopeType: "parent_group",
                scopeId: cell.parentGroup,
                scopeLabel: cell.parentGroup,
                canBulkApply: true,
            };
        }
    }

    return null;
}

function bottomUpOverrideScopeForAssumptionScope(scope: ForecastAssumptionScope): WorkbenchBottomUpOverrideScope {
    if (scope.kind === "revenue") {
        return {
            kind: "revenue",
            scope_type: scope.scopeType,
            scope_id: scope.scopeId,
            scope_label: scope.scopeLabel,
            account_id: scope.accountId,
        };
    }
    return {
        kind: "cost",
        pnl_section: scope.pnlSection,
        scope_type: scope.scopeType,
        scope_id: scope.scopeId,
        scope_label: scope.scopeLabel,
        account_id: scope.accountId,
    };
}

function canUseExactBottomUpOverride(scope: ForecastAssumptionScope, cell: SelectedWorkbenchCell): boolean {
    if (cell.isExpandable) return false;
    if (scope.kind === "revenue") return scope.scopeType === "customer";
    if (scope.scopeType === "supplier") return Boolean(scope.accountId);
    if (scope.scopeType === "account") return Boolean(scope.accountId);
    return false;
}

function isBottomUpOverrideOpForScope(
    op: ModelingWorkbenchOpHistoryItem,
    scope: ForecastAssumptionScope,
    monthKey: string,
): boolean {
    const params = op.params ?? {};
    if (params.source !== "workbench_bottom_up_override") return false;
    if (params.month_key !== monthKey) return false;
    if (params.kind !== scope.kind) return false;
    if (params.scope_type !== scope.scopeType) return false;
    if (params.scope_id !== scope.scopeId) return false;
    if (scope.kind === "cost") {
        if (params.pnl_section !== scope.pnlSection) return false;
        if (scope.accountId && params.account_id !== scope.accountId) return false;
    }
    if (scope.kind === "revenue" && scope.accountId && params.account_id !== scope.accountId) {
        return false;
    }
    return true;
}

function bottomUpFormulaSummary(
    scope: ForecastAssumptionScope,
    cell: SelectedWorkbenchCell,
    hasActiveOverride: boolean,
    activeOverrideValue: unknown,
    baselineMethod: string,
): string {
    const scopeType = scope.kind === "revenue" ? scope.scopeType : scope.scopeType.replace("_", " ");
    const scopeReference = `${scopeType}: ${scope.scopeLabel}`;
    const baseline = baselineMethodFormula(baselineMethod);
    const overrideNumber = Number(activeOverrideValue);
    if (hasActiveOverride && Number.isFinite(overrideNumber)) {
        return `= ${formatMetricValue(overrideNumber, "currency")} exact ${cell.monthLabel} leaf override; replaces ${baseline} + selected driver/template for ${scopeReference}. Ops & Revisions keeps the audit trail.`;
    }
    return `= ${baseline} for ${scopeReference} + selected driver/template + active Ops & Revisions for ${cell.monthLabel}`;
}

function buildRevenueParams(driverType: RevenueDriverType, rawValue: string): Record<string, unknown> {
    const parsed = Number(rawValue.trim());
    if (driverType === "flat") return {};
    if (!Number.isFinite(parsed)) return {};
    if (driverType === "last_year_curve") return {uplift_pct: parsed};
    return {value: parsed};
}

function hasValidDriverValue(driverType: RevenueDriverType, rawValue: string): boolean {
    if (driverType === "flat") return true;
    return Number.isFinite(Number(rawValue.trim()));
}

function buildMonthOverrides(rawOverrides: Record<string, string>): Record<string, number> {
    const overrides: Record<string, number> = {};
    for (const [monthKey, rawValue] of Object.entries(rawOverrides)) {
        const trimmed = rawValue.trim();
        if (!trimmed) continue;
        const parsed = Number(trimmed);
        if (!Number.isFinite(parsed)) continue;
        overrides[monthKey] = parsed;
    }
    return overrides;
}

function driverValueFromConfig(driverType: RevenueDriverType, params: Record<string, unknown>): string {
    if (driverType === "last_year_curve") {
        return params.uplift_pct != null ? String(params.uplift_pct) : "";
    }
    if (driverType === "flat") return "";
    return params.value != null ? String(params.value) : "";
}

type DriverAssumptionConfig = Pick<CustomerRevenueAssumption, "template_id" | "driver_type" | "effective_driver_type" | "params" | "effective_params">;
type DriverTemplateConfig = Pick<RevenueForecastTemplate, "driver_type" | "params">;

function driverValueFromAssumption(assumption: DriverAssumptionConfig | null): string {
    if (!assumption) return "";
    const driverType = (
        assumption.template_id
            ? assumption.effective_driver_type
            : (assumption.driver_type || assumption.effective_driver_type)
    ) as RevenueDriverType;
    const params = assumption.template_id ? assumption.effective_params : assumption.params;
    return driverValueFromConfig(driverType, params);
}

function driverValueFromTemplate(template: DriverTemplateConfig | null): string {
    if (!template) return "";
    return driverValueFromConfig(template.driver_type, template.params);
}

function driverValueLabel(driverType: RevenueDriverType, noun = "revenue"): string {
    if (driverType === "growth") return "Growth %";
    if (driverType === "fixed") return `Monthly ${noun}`;
    if (driverType === "last_year_curve") return "Uplift %";
    return "Value";
}

export function RevenuePlanningSection({
    workspaceId,
    scenarioId,
    actualMonths,
    forecastMonths,
    requestedScopeKey,
    onModelRefresh,
}: {
    workspaceId: string;
    scenarioId: string;
    actualMonths: number;
    forecastMonths: number;
    requestedScopeKey?: string | null;
    onModelRefresh: () => Promise<unknown>;
}) {
    const {grid, isLoading, error, refresh: refreshGrid} = useRevenueBottomUp(
        workspaceId,
        scenarioId,
        actualMonths,
        forecastMonths,
    );
    const {assumptions, isLoading: assumptionsLoading, refresh: refreshAssumptions} = useCustomerAssumptions(
        workspaceId,
        scenarioId,
    );
    const {templates, refresh: refreshTemplates} = useRevenueTemplates(workspaceId);
    const [scopeKey, setScopeKey] = useState("");
    const [assumptionMode, setAssumptionMode] = useState<RevenueAssumptionMode>("direct");
    const [templateId, setTemplateId] = useState("");
    const [templateName, setTemplateName] = useState("");
    const [templateDescription, setTemplateDescription] = useState("");
    const [driverType, setDriverType] = useState<RevenueDriverType>("growth");
    const [driverValue, setDriverValue] = useState("");
    const [notes, setNotes] = useState("");
    const [editingTemplateId, setEditingTemplateId] = useState("");
    const [editingTemplateName, setEditingTemplateName] = useState("");
    const [editingTemplateDescription, setEditingTemplateDescription] = useState("");
    const [editingTemplateDriverType, setEditingTemplateDriverType] = useState<RevenueDriverType>("growth");
    const [editingTemplateDriverValue, setEditingTemplateDriverValue] = useState("");
    const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
    const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);
    const [monthOverrides, setMonthOverrides] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [isBulkApplying, setIsBulkApplying] = useState(false);
    const [archivingTemplateId, setArchivingTemplateId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [mutationError, setMutationError] = useState<string | null>(null);

    const segmentRows = grid?.segments ?? EMPTY_SEGMENT_ROWS;
    const actualMonthKeys = grid?.actual_months ?? EMPTY_MONTH_KEYS;
    const forecastMonthKeys = grid?.forecast_months ?? EMPTY_MONTH_KEYS;
    const scopeOptions = useMemo<RevenueScopeOption[]>(() => {
        const options: RevenueScopeOption[] = [];
        for (const segment of segmentRows) {
            options.push({
                key: `segment:${segment.label}`,
                scopeType: "segment",
                scopeId: segment.label,
                scopeLabel: segment.label,
                description: "Segment assumption",
            });
            for (const parent of segment.parents) {
                options.push({
                    key: `parent:${parent.id}`,
                    scopeType: "parent",
                    scopeId: parent.id,
                    scopeLabel: parent.name,
                    description: `Parent in ${segment.label}`,
                });
                for (const customer of parent.children) {
                    options.push({
                        key: `customer:${customer.id}`,
                        scopeType: "customer",
                        scopeId: customer.id,
                        scopeLabel: customer.name,
                        description: `${parent.name} · ${segment.label}`,
                    });
                }
            }
        }
        return options;
    }, [segmentRows]);
    const assumptionByScope = useMemo(() => {
        const map = new Map<string, CustomerRevenueAssumption>();
        for (const assumption of assumptions) {
            map.set(`${assumption.scope_type}:${assumption.scope_id}`, assumption);
        }
        return map;
    }, [assumptions]);
    const selectedScope = scopeOptions.find((option) => option.key === scopeKey) ?? null;
    const selectedAssumption = selectedScope
        ? assumptionByScope.get(`${selectedScope.scopeType}:${selectedScope.scopeId}`) ?? null
        : null;
    const selectedTemplate = templates.find((template) => template.id === templateId) ?? null;
    const editableTemplate = templates.find((template) => template.id === editingTemplateId) ?? null;
    const canBulkApply = selectedScope != null && selectedScope.scopeType !== "customer";
    const templateUsageCount = useMemo(() => {
        const usage = new Map<string, number>();
        for (const assumption of assumptions) {
            if (!assumption.template_id) continue;
            usage.set(assumption.template_id, (usage.get(assumption.template_id) ?? 0) + 1);
        }
        return usage;
    }, [assumptions]);
    const selectedSegment = useMemo(() => {
        if (!selectedScope) return null;
        for (const segment of segmentRows) {
            if (selectedScope.scopeType === "segment" && segment.label === selectedScope.scopeId) {
                return segment;
            }
            for (const parent of segment.parents) {
                if (selectedScope.scopeType === "parent" && parent.id === selectedScope.scopeId) {
                    return segment;
                }
                if (selectedScope.scopeType === "customer" && parent.children.some((customer) => customer.id === selectedScope.scopeId)) {
                    return segment;
                }
            }
        }
        return null;
    }, [segmentRows, selectedScope]);
    const selectedParent = useMemo(() => {
        if (!selectedScope) return null;
        for (const segment of segmentRows) {
            for (const parent of segment.parents) {
                if (selectedScope.scopeType === "parent" && parent.id === selectedScope.scopeId) {
                    return parent;
                }
                if (selectedScope.scopeType === "customer" && parent.children.some((customer) => customer.id === selectedScope.scopeId)) {
                    return parent;
                }
            }
        }
        return null;
    }, [segmentRows, selectedScope]);
    const visibleParents = useMemo(() => {
        if (!selectedScope) return [];
        if (selectedScope.scopeType === "segment") return selectedSegment?.parents ?? [];
        if (selectedScope.scopeType === "parent" || selectedScope.scopeType === "customer") {
            return selectedParent ? [selectedParent] : [];
        }
        return [];
    }, [selectedParent, selectedScope, selectedSegment]);

    useEffect(() => {
        if (!scopeOptions.length) {
            setScopeKey("");
            return;
        }
        setScopeKey((current) => current || scopeOptions[0].key);
    }, [scopeOptions]);

    useEffect(() => {
        if (requestedScopeKey && scopeOptions.some((option) => option.key === requestedScopeKey)) {
            setScopeKey(requestedScopeKey);
        }
    }, [requestedScopeKey, scopeOptions]);

    useEffect(() => {
        if (!templates.length) {
            setEditingTemplateId("");
            setEditingTemplateName("");
            setEditingTemplateDescription("");
            setEditingTemplateDriverType("growth");
            setEditingTemplateDriverValue("");
            return;
        }
        setEditingTemplateId((current) => (
            current && templates.some((template) => template.id === current)
                ? current
                : templates[0].id
        ));
    }, [templates]);

    useEffect(() => {
        if (!selectedAssumption) {
            setAssumptionMode("direct");
            setTemplateId("");
            setTemplateName("");
            setTemplateDescription("");
            setDriverType("growth");
            setDriverValue("");
            setNotes("");
            setMonthOverrides(Object.fromEntries(forecastMonthKeys.map((monthKey) => [monthKey, ""])));
            return;
        }
        setAssumptionMode(selectedAssumption.template_id ? "template" : "direct");
        setTemplateId(selectedAssumption.template_id ?? "");
        setTemplateName("");
        setTemplateDescription("");
        setDriverType((selectedAssumption.template_id
            ? selectedAssumption.effective_driver_type
            : (selectedAssumption.driver_type || selectedAssumption.effective_driver_type)
        ) as RevenueDriverType);
        setDriverValue(driverValueFromAssumption(selectedAssumption));
        setNotes(selectedAssumption.notes || "");
        setMonthOverrides(
            Object.fromEntries(
                forecastMonthKeys.map((monthKey) => [
                    monthKey,
                    selectedAssumption.month_overrides?.[monthKey] != null
                        ? String(selectedAssumption.month_overrides[monthKey])
                        : "",
                ]),
            ),
        );
    }, [forecastMonthKeys, selectedAssumption]);

    useEffect(() => {
        if (!editableTemplate) return;
        setEditingTemplateName(editableTemplate.name);
        setEditingTemplateDescription(editableTemplate.description || "");
        setEditingTemplateDriverType(editableTemplate.driver_type);
        setEditingTemplateDriverValue(driverValueFromTemplate(editableTemplate));
    }, [editableTemplate]);

    const refreshRevenueData = async () => Promise.all([refreshGrid(), refreshAssumptions(), onModelRefresh()]);

    const handleCreateTemplate = async () => {
        if (assumptionMode !== "direct") return;
        if (!templateName.trim()) {
            setMutationError("Template name is required.");
            return;
        }
        if (!hasValidDriverValue(driverType, driverValue)) {
            setMutationError(`${driverValueLabel(driverType)} is required.`);
            return;
        }

        setIsCreatingTemplate(true);
        setMutationError(null);
        try {
            const result = await createRevenueTemplate(workspaceId, {
                name: templateName.trim(),
                description: templateDescription.trim(),
                driver_type: driverType,
                params: buildRevenueParams(driverType, driverValue),
            });
            await refreshTemplates();
            const createdTemplateId = result?.data?.template?.id ?? "";
            if (createdTemplateId) {
                setTemplateId(createdTemplateId);
                setEditingTemplateId(createdTemplateId);
                setAssumptionMode("template");
            }
            setTemplateName("");
            setTemplateDescription("");
            toast.success("Revenue template created");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to create revenue template";
            setMutationError(message);
            toast.error(message);
        } finally {
            setIsCreatingTemplate(false);
        }
    };

    const handleUpdateTemplate = async () => {
        if (!editableTemplate) return;
        if (!editingTemplateName.trim()) {
            setMutationError("Template name is required.");
            return;
        }
        if (!hasValidDriverValue(editingTemplateDriverType, editingTemplateDriverValue)) {
            setMutationError(`${driverValueLabel(editingTemplateDriverType)} is required.`);
            return;
        }

        setIsUpdatingTemplate(true);
        setMutationError(null);
        try {
            await updateRevenueTemplate(workspaceId, editableTemplate.id, {
                name: editingTemplateName.trim(),
                description: editingTemplateDescription.trim(),
                driver_type: editingTemplateDriverType,
                params: buildRevenueParams(editingTemplateDriverType, editingTemplateDriverValue),
            });
            await refreshTemplates();
            toast.success("Revenue template updated");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to update revenue template";
            setMutationError(message);
            toast.error(message);
        } finally {
            setIsUpdatingTemplate(false);
        }
    };

    const handleArchiveTemplate = async (template: RevenueForecastTemplate) => {
        const activeUsageCount = templateUsageCount.get(template.id) ?? 0;
        if (activeUsageCount > 0) {
            setMutationError(`Template "${template.name}" is still used by ${activeUsageCount} active assumption${activeUsageCount === 1 ? "" : "s"}.`);
            return;
        }
        if (!window.confirm(`Archive template "${template.name}"?`)) return;

        setArchivingTemplateId(template.id);
        setMutationError(null);
        try {
            await archiveRevenueTemplate(workspaceId, template.id);
            if (templateId === template.id) setTemplateId("");
            await refreshTemplates();
            toast.success("Revenue template archived");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to archive revenue template";
            setMutationError(message);
            toast.error(message);
        } finally {
            setArchivingTemplateId(null);
        }
    };

    const handleSaveAssumption = async () => {
        if (!selectedScope) return;
        if (assumptionMode === "template" && !templateId) {
            setMutationError("Template is required.");
            return;
        }
        if (assumptionMode === "direct" && !hasValidDriverValue(driverType, driverValue)) {
            setMutationError(`${driverValueLabel(driverType)} is required.`);
            return;
        }

        setIsSaving(true);
        setMutationError(null);
        try {
            const overrides = buildMonthOverrides(monthOverrides);
            if (selectedAssumption) {
                if (assumptionMode === "template") {
                    await updateCustomerAssumption(workspaceId, selectedAssumption.id, {
                        template_id: templateId,
                        month_overrides: overrides,
                        notes,
                    });
                } else {
                    await updateCustomerAssumption(workspaceId, selectedAssumption.id, {
                        driver_type: driverType,
                        params: buildRevenueParams(driverType, driverValue),
                        month_overrides: overrides,
                        notes,
                        template_id: null,
                    });
                }
            } else {
                if (assumptionMode === "template") {
                    await saveCustomerAssumption(workspaceId, {
                        scenario_id: scenarioId,
                        scope_type: selectedScope.scopeType,
                        scope_id: selectedScope.scopeId,
                        scope_label: selectedScope.scopeLabel,
                        template_id: templateId,
                        month_overrides: overrides,
                        notes,
                    });
                } else {
                    await saveCustomerAssumption(workspaceId, {
                        scenario_id: scenarioId,
                        scope_type: selectedScope.scopeType,
                        scope_id: selectedScope.scopeId,
                        scope_label: selectedScope.scopeLabel,
                        driver_type: driverType,
                        params: buildRevenueParams(driverType, driverValue),
                        month_overrides: overrides,
                        notes,
                    });
                }
            }
            await refreshRevenueData();
            toast.success(selectedAssumption ? "Revenue assumption updated" : "Revenue assumption created");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to save revenue assumption";
            setMutationError(message);
            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteAssumption = async (assumption: CustomerRevenueAssumption) => {
        setDeletingId(assumption.id);
        setMutationError(null);
        try {
            await deleteCustomerAssumption(workspaceId, assumption.id);
            await refreshRevenueData();
            toast.success("Revenue assumption removed");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to delete revenue assumption";
            setMutationError(message);
            toast.error(message);
        } finally {
            setDeletingId(null);
        }
    };

    const handleBulkApply = async () => {
        if (!selectedScope || !canBulkApply) return;
        if (assumptionMode === "template" && !templateId) {
            setMutationError("Template is required.");
            return;
        }
        if (assumptionMode === "direct" && !hasValidDriverValue(driverType, driverValue)) {
            setMutationError(`${driverValueLabel(driverType)} is required.`);
            return;
        }
        const bulkScopeType = selectedScope.scopeType === "segment" ? "segment" : "parent";

        setIsBulkApplying(true);
        setMutationError(null);
        try {
            await revenueBulkApply(workspaceId, {
                scenario_id: scenarioId,
                scope_type: bulkScopeType,
                scope_id: selectedScope.scopeId,
                driver_type: assumptionMode === "direct" ? driverType : undefined,
                template_id: assumptionMode === "template" ? templateId : undefined,
                params: assumptionMode === "direct" ? buildRevenueParams(driverType, driverValue) : undefined,
                notes: notes || `Bulk applied from ${selectedScope.scopeLabel}`,
            });
            await refreshRevenueData();
            toast.success(`Bulk-applied revenue assumptions from ${selectedScope.scopeLabel}`);
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to bulk apply revenue assumptions";
            setMutationError(message);
            toast.error(message);
        } finally {
            setIsBulkApplying(false);
        }
    };

    return (
        <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[2px] text-sky-700">Phase 7 | Revenue Planning</p>
                    <h2 className="mt-1 text-lg font-semibold text-slate-900">Bottom-up revenue inside Workbench</h2>
                    <p className="mt-1 text-sm text-slate-500">Customer, parent, and segment assumptions feed the same 3-statement engine used by the statement grids below.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                        {(assumptionsLoading ? "…" : assumptions.length)} active assumptions
                    </Badge>
                    <Badge className="bg-sky-50 text-sky-700 hover:bg-sky-50">
                        {forecastMonthKeys.length} forecast months
                    </Badge>
                </div>
            </div>

            <div className="mt-4 space-y-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4" data-tour="workbench-revenue-assumption-form">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <p className="text-sm font-medium text-slate-900">Edit revenue assumptions</p>
                            <p className="mt-1 text-sm text-slate-500">Create or update one scoped assumption and rerun the same Workbench model below.</p>
                        </div>
                        <Badge className="bg-white text-slate-700 hover:bg-white">
                            {templates.length} templates available
                        </Badge>
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <div className="space-y-2" data-tour="workbench-revenue-scope">
                            <Label htmlFor="revenue-scope">Scope</Label>
                            <Select value={scopeKey} onValueChange={setScopeKey} disabled={isLoading || scopeOptions.length === 0}>
                                <SelectTrigger id="revenue-scope" className="bg-white">
                                    <SelectValue placeholder={isLoading ? "Loading…" : scopeOptions.length === 0 && !error ? "No customer map configured" : "Select scope"} />
                                </SelectTrigger>
                                <SelectContent>
                                    {scopeOptions.map((option) => (
                                        <SelectItem key={option.key} value={option.key}>
                                            {option.scopeLabel} · {option.description}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2" data-tour="workbench-revenue-input-mode">
                            <Label htmlFor="revenue-assumption-mode">Input mode</Label>
                            <Select value={assumptionMode} onValueChange={(value) => setAssumptionMode(value as RevenueAssumptionMode)}>
                                <SelectTrigger id="revenue-assumption-mode" className="bg-white">
                                    <SelectValue placeholder="Select mode" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="direct">Direct driver</SelectItem>
                                    <SelectItem value="template">Saved template</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2" data-tour="workbench-revenue-template-selector">
                            <Label htmlFor="revenue-template">Template</Label>
                            <Select value={templateId} onValueChange={setTemplateId} disabled={assumptionMode !== "template"}>
                                <SelectTrigger id="revenue-template" className="bg-white">
                                    <SelectValue placeholder={templates.length ? "Select template" : "No templates available"} />
                                </SelectTrigger>
                                <SelectContent>
                                    {templates.map((template) => (
                                        <SelectItem key={template.id} value={template.id}>
                                            {template.name} · {template.driver_type}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2" data-tour="workbench-revenue-driver-type">
                            <Label htmlFor="revenue-driver">Driver</Label>
                            <Select
                                value={driverType}
                                onValueChange={(value) => setDriverType(value as RevenueDriverType)}
                                disabled={assumptionMode === "template"}
                            >
                                <SelectTrigger id="revenue-driver" className="bg-white">
                                    <SelectValue placeholder="Select driver" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="growth">Growth</SelectItem>
                                    <SelectItem value="fixed">Fixed</SelectItem>
                                    <SelectItem value="flat">Flat</SelectItem>
                                    <SelectItem value="last_year_curve">Last Year Curve</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2" data-tour="workbench-revenue-driver-value">
                            <Label htmlFor="revenue-driver-value">{driverValueLabel(driverType)}</Label>
                            <Input
                                id="revenue-driver-value"
                                value={driverValue}
                                onChange={(event) => setDriverValue(event.target.value)}
                                disabled={assumptionMode === "template" || driverType === "flat"}
                                placeholder={driverType === "growth" ? "10" : driverType === "fixed" ? "250000" : driverType === "last_year_curve" ? "5" : "Inherited from actuals"}
                                className="bg-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="revenue-notes">Notes</Label>
                            <Input
                                id="revenue-notes"
                                value={notes}
                                onChange={(event) => setNotes(event.target.value)}
                                placeholder="Optional rationale"
                                className="bg-white"
                            />
                        </div>
                    </div>
                    <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4" data-tour="workbench-revenue-save-template">
                        <p className="text-sm font-medium text-slate-900">Save current driver as template</p>
                        <p className="mt-1 text-sm text-slate-500">
                            {assumptionMode === "direct"
                                ? "Create a reusable revenue template from the current direct-driver settings."
                                : "Switch Input mode to Direct driver to save the current setup as a reusable template."}
                        </p>
                        <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                            <div className="space-y-2">
                                <Label htmlFor="revenue-template-name">Template name</Label>
                                <Input
                                    id="revenue-template-name"
                                    value={templateName}
                                    onChange={(event) => setTemplateName(event.target.value)}
                                    placeholder="e.g. Conservative growth"
                                    className="bg-white"
                                    disabled={assumptionMode !== "direct"}
                                />
                            </div>
                            <div className="space-y-2 xl:col-span-2">
                                <Label htmlFor="revenue-template-description">Description</Label>
                                <Input
                                    id="revenue-template-description"
                                    value={templateDescription}
                                    onChange={(event) => setTemplateDescription(event.target.value)}
                                    placeholder="Optional notes for reuse"
                                    className="bg-white"
                                    disabled={assumptionMode !== "direct"}
                                />
                            </div>
                        </div>
                        <div className="mt-3">
                            <Button
                                variant="outline"
                                onClick={handleCreateTemplate}
                                disabled={assumptionMode !== "direct" || isCreatingTemplate || isSaving || isBulkApplying}
                            >
                                {isCreatingTemplate ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                                Save as template
                            </Button>
                        </div>
                    </div>
                    <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4" data-tour="workbench-revenue-manage-templates">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <p className="text-sm font-medium text-slate-900">Manage saved templates</p>
                                <p className="mt-1 text-sm text-slate-500">Edit reusable drivers here and keep the planner aligned with active template logic.</p>
                            </div>
                            <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                                {templates.length} active templates
                            </Badge>
                        </div>
                        {templates.length ? (
                            <>
                                <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="revenue-template-editor">Template</Label>
                                        <Select value={editingTemplateId} onValueChange={setEditingTemplateId}>
                                            <SelectTrigger id="revenue-template-editor" className="bg-white">
                                                <SelectValue placeholder="Select template" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {templates.map((template) => (
                                                    <SelectItem key={template.id} value={template.id}>
                                                        {template.name} · {template.driver_type}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="revenue-template-editor-name">Name</Label>
                                        <Input
                                            id="revenue-template-editor-name"
                                            value={editingTemplateName}
                                            onChange={(event) => setEditingTemplateName(event.target.value)}
                                            placeholder="Template name"
                                            className="bg-white"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="revenue-template-editor-driver">Driver</Label>
                                        <Select
                                            value={editingTemplateDriverType}
                                            onValueChange={(value) => setEditingTemplateDriverType(value as RevenueDriverType)}
                                        >
                                            <SelectTrigger id="revenue-template-editor-driver" className="bg-white">
                                                <SelectValue placeholder="Select driver" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="growth">Growth</SelectItem>
                                                <SelectItem value="fixed">Fixed</SelectItem>
                                                <SelectItem value="flat">Flat</SelectItem>
                                                <SelectItem value="last_year_curve">Last Year Curve</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="revenue-template-editor-value">{driverValueLabel(editingTemplateDriverType)}</Label>
                                        <Input
                                            id="revenue-template-editor-value"
                                            value={editingTemplateDriverValue}
                                            onChange={(event) => setEditingTemplateDriverValue(event.target.value)}
                                            disabled={editingTemplateDriverType === "flat"}
                                            placeholder={editingTemplateDriverType === "growth" ? "10" : editingTemplateDriverType === "fixed" ? "250000" : editingTemplateDriverType === "last_year_curve" ? "5" : "Inherited from actuals"}
                                            className="bg-white"
                                        />
                                    </div>
                                    <div className="space-y-2 md:col-span-2 xl:col-span-4">
                                        <Label htmlFor="revenue-template-editor-description">Description</Label>
                                        <Input
                                            id="revenue-template-editor-description"
                                            value={editingTemplateDescription}
                                            onChange={(event) => setEditingTemplateDescription(event.target.value)}
                                            placeholder="Optional notes for reuse"
                                            className="bg-white"
                                        />
                                    </div>
                                </div>
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                    <Button variant="outline" onClick={handleUpdateTemplate} disabled={isUpdatingTemplate || !editableTemplate}>
                                        {isUpdatingTemplate ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                                        Update template
                                    </Button>
                                    {editableTemplate ? (
                                        <Button
                                            variant="outline"
                                            disabled={archivingTemplateId === editableTemplate.id || (templateUsageCount.get(editableTemplate.id) ?? 0) > 0}
                                            onClick={() => handleArchiveTemplate(editableTemplate)}
                                        >
                                            {archivingTemplateId === editableTemplate.id ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                                            Archive template
                                        </Button>
                                    ) : null}
                                    {editableTemplate ? (
                                        <span className="text-sm text-slate-500">
                                            {(templateUsageCount.get(editableTemplate.id) ?? 0) > 0
                                                ? `Used by ${templateUsageCount.get(editableTemplate.id) ?? 0} active assumption${(templateUsageCount.get(editableTemplate.id) ?? 0) === 1 ? "" : "s"}`
                                                : "Unused template"}
                                        </span>
                                    ) : null}
                                </div>
                                <div className="mt-4 rounded-xl border border-slate-200">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Template</TableHead>
                                                <TableHead>Driver</TableHead>
                                                <TableHead>Description</TableHead>
                                                <TableHead className="text-right">Usage</TableHead>
                                                <TableHead className="text-right">Action</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {templates.map((template) => (
                                                <TableRow key={template.id}>
                                                    <TableCell className="font-medium text-slate-900">{template.name}</TableCell>
                                                    <TableCell>{template.driver_type}</TableCell>
                                                    <TableCell className="max-w-[320px] truncate text-sm text-slate-500">{template.description || "—"}</TableCell>
                                                    <TableCell className="text-right tabular-nums">{templateUsageCount.get(template.id) ?? 0}</TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <Button size="sm" variant="outline" onClick={() => setEditingTemplateId(template.id)}>
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                disabled={archivingTemplateId === template.id || (templateUsageCount.get(template.id) ?? 0) > 0}
                                                                onClick={() => handleArchiveTemplate(template)}
                                                            >
                                                                {archivingTemplateId === template.id ? <Loader2Icon className="mr-2 size-3.5 animate-spin" /> : null}
                                                                Archive
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </>
                        ) : (
                            <div className="mt-3 rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                                No active templates yet. Save a direct driver as a template to reuse it across scopes.
                            </div>
                        )}
                    </div>
                    <div className="mt-4" data-tour="workbench-revenue-month-overrides">
                        <p className="text-sm font-medium text-slate-900">Month overrides</p>
                        <p className="mt-1 text-sm text-slate-500">Optional forecast-month overrides on top of the selected driver or template.</p>
                        <div className="mt-3 grid gap-3 md:grid-cols-3 xl:grid-cols-6">
                            {forecastMonthKeys.map((monthKey) => (
                                <div key={monthKey} className="space-y-2">
                                    <Label htmlFor={`revenue-override-${monthKey}`}>{monthKey}</Label>
                                    <Input
                                        id={`revenue-override-${monthKey}`}
                                        value={monthOverrides[monthKey] ?? ""}
                                        onChange={(event) => setMonthOverrides((current) => ({
                                            ...current,
                                            [monthKey]: event.target.value,
                                        }))}
                                        placeholder="Optional override"
                                        className="bg-white"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2" data-tour="workbench-revenue-save-actions">
                        <Button onClick={handleSaveAssumption} disabled={isSaving || isBulkApplying || !selectedScope}>
                            {isSaving ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                            {selectedAssumption ? "Update assumption" : "Create assumption"}
                        </Button>
                        {canBulkApply ? (
                            <Button variant="outline" onClick={handleBulkApply} disabled={isSaving || isBulkApplying}>
                                {isBulkApplying ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                                Bulk apply to child customers
                            </Button>
                        ) : null}
                        {selectedAssumption ? (
                            <Button
                                variant="outline"
                                disabled={deletingId === selectedAssumption.id || isSaving || isBulkApplying}
                                onClick={() => handleDeleteAssumption(selectedAssumption)}
                            >
                                {deletingId === selectedAssumption.id ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                                Remove assumption
                            </Button>
                        ) : null}
                        {selectedScope ? (
                            <span className="text-sm text-slate-500">
                                Editing {selectedScope.scopeLabel}
                                {selectedAssumption
                                    ? selectedAssumption.template_id
                                        ? ` · current template ${selectedAssumption.template_id}`
                                        : ` · current driver ${driverType}`
                                    : " · no active assumption yet"}
                            </span>
                        ) : null}
                    </div>
                    {assumptionMode === "template" && selectedTemplate ? (
                        <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
                            Using template <span className="font-medium">{selectedTemplate.name}</span> · {selectedTemplate.driver_type}
                        </div>
                    ) : null}
                    {mutationError ? (
                        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                            {mutationError}
                        </div>
                    ) : null}
                </div>

                <div data-tour="workbench-revenue-tables">
                {isLoading && <Skeleton className="h-40 w-full rounded-2xl" />}
                {error && <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}
                {!isLoading && !error && grid && (
                    <>
                        <div className="grid gap-3 md:grid-cols-3">
                            <Card className="border-slate-200 shadow-none">
                                <CardContent className="p-4">
                                    <p className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-500">Actual Revenue</p>
                                    <p className="mt-2 text-xl font-semibold text-slate-950">
                                        {formatMetricValue(sumMonthValues(grid.grand_total, actualMonthKeys), "currency")}
                                    </p>
                                </CardContent>
                            </Card>
                            <Card className="border-slate-200 shadow-none">
                                <CardContent className="p-4">
                                    <p className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-500">Forecast Revenue</p>
                                    <p className="mt-2 text-xl font-semibold text-slate-950">
                                        {formatMetricValue(sumMonthValues(grid.grand_total, forecastMonthKeys), "currency")}
                                    </p>
                                </CardContent>
                            </Card>
                            <Card className="border-slate-200 shadow-none">
                                <CardContent className="p-4">
                                    <p className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-500">Coverage</p>
                                    <p className="mt-2 text-xl font-semibold text-slate-950">
                                        {segmentRows.length} segments
                                    </p>
                                </CardContent>
                            </Card>
                        </div>

                        <div className="rounded-xl border border-slate-200">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Segment</TableHead>
                                        <TableHead className="text-right">Actual Total</TableHead>
                                        <TableHead className="text-right">Forecast Total</TableHead>
                                        <TableHead className="text-right">Parents</TableHead>
                                        <TableHead className="text-right">Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {segmentRows.map((segment) => (
                                        <TableRow key={segment.label}>
                                            <TableCell className="font-medium text-slate-900">{segment.label}</TableCell>
                                            <TableCell className="text-right tabular-nums">
                                                {formatMetricValue(sumMonthValues(segment.months, actualMonthKeys), "currency")}
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">
                                                {formatMetricValue(sumMonthValues(segment.months, forecastMonthKeys), "currency")}
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">{segment.parents.length}</TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setScopeKey(`segment:${segment.label}`)}
                                                >
                                                    Edit
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {segmentRows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} className="py-8 text-center text-sm text-slate-500">
                                                No bottom-up revenue assumptions yet.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>

                        <div className="rounded-xl border border-slate-200">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Parent</TableHead>
                                        <TableHead className="text-right">Actual Total</TableHead>
                                        <TableHead className="text-right">Forecast Total</TableHead>
                                        <TableHead className="text-right">Customers</TableHead>
                                        <TableHead className="text-right">Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {visibleParents.map((parent) => (
                                        <TableRow key={parent.id}>
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium text-slate-900">{parent.name}</p>
                                                    <p className="text-xs text-slate-500">{selectedSegment?.label ?? "Unmapped"}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">
                                                {formatMetricValue(sumMonthValues(parent.months, actualMonthKeys), "currency")}
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">
                                                {formatMetricValue(sumMonthValues(parent.months, forecastMonthKeys), "currency")}
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">{parent.children.length}</TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setScopeKey(`parent:${parent.id}`)}
                                                >
                                                    Edit
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {visibleParents.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} className="py-8 text-center text-sm text-slate-500">
                                                Select a segment, parent, or customer scope to inspect the hierarchy.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>

                        {selectedParent ? (
                            <div className="rounded-xl border border-slate-200">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Customer</TableHead>
                                            <TableHead>Driver</TableHead>
                                            <TableHead className="text-right">Actual Total</TableHead>
                                            <TableHead className="text-right">Forecast Total</TableHead>
                                            <TableHead className="text-right">Assumption</TableHead>
                                            <TableHead className="text-right">Action</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {selectedParent.children.map((customer) => (
                                            <TableRow key={customer.id}>
                                                <TableCell>
                                                    <div>
                                                        <p className="font-medium text-slate-900">{customer.name}</p>
                                                        <p className="text-xs text-slate-500">{customer.id}</p>
                                                    </div>
                                                </TableCell>
                                                <TableCell>{customer.driver_type || "flat"}</TableCell>
                                                <TableCell className="text-right tabular-nums">
                                                    {formatMetricValue(sumMonthValues(customer.actuals, actualMonthKeys), "currency")}
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums">
                                                    {formatMetricValue(sumMonthValues(customer.forecast, forecastMonthKeys), "currency")}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {customer.has_assumption ? (
                                                        <Badge className="bg-sky-50 text-sky-700 hover:bg-sky-50">active</Badge>
                                                    ) : (
                                                        <span className="text-sm text-slate-400">—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setScopeKey(`customer:${customer.id}`)}
                                                    >
                                                        Edit
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : null}

                        <div className="rounded-xl border border-slate-200">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Scope</TableHead>
                                        <TableHead>Driver</TableHead>
                                        <TableHead>Value</TableHead>
                                        <TableHead>Notes</TableHead>
                                        <TableHead className="text-right">Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {assumptions.map((assumption) => (
                                        <TableRow key={assumption.id}>
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium text-slate-900">{assumption.scope_label || assumption.scope_id}</p>
                                                    <p className="text-xs text-slate-500">{assumption.scope_type}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>{assumption.template_id ? assumption.effective_driver_type : assumption.driver_type || assumption.effective_driver_type}</TableCell>
                                            <TableCell>
                                                {assumption.template_id ? "Template" : (driverValueFromAssumption(assumption) || "—")}
                                            </TableCell>
                                            <TableCell className="max-w-[280px] truncate text-sm text-slate-500">{assumption.notes || "—"}</TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setScopeKey(`${assumption.scope_type}:${assumption.scope_id}`)}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        disabled={deletingId === assumption.id}
                                                        onClick={() => handleDeleteAssumption(assumption)}
                                                    >
                                                        {deletingId === assumption.id ? <Loader2Icon className="mr-2 size-3.5 animate-spin" /> : null}
                                                        Remove
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {assumptions.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} className="py-8 text-center text-sm text-slate-500">
                                                No active revenue assumptions saved yet.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </>
                )}
                </div>
            </div>
        </section>
    );
}

export function CostPlanningSection({
    workspaceId,
    scenarioId,
    actualMonths,
    forecastMonths,
    requestedScopeKey,
    onModelRefresh,
}: {
    workspaceId: string;
    scenarioId: string;
    actualMonths: number;
    forecastMonths: number;
    requestedScopeKey?: string | null;
    onModelRefresh: () => Promise<unknown>;
}) {
    const {grid, isLoading, error, refresh: refreshGrid} = useCostBottomUp(
        workspaceId,
        scenarioId,
        actualMonths,
        forecastMonths,
    );
    const {assumptions, isLoading: assumptionsLoading, refresh: refreshAssumptions} = useCostAssumptions(
        workspaceId,
        scenarioId,
    );
    const {templates, refresh: refreshTemplates} = useCostTemplates(workspaceId);
    const [scopeKey, setScopeKey] = useState("");
    const [assumptionMode, setAssumptionMode] = useState<CostAssumptionMode>("direct");
    const [templateId, setTemplateId] = useState("");
    const [templateName, setTemplateName] = useState("");
    const [templateDescription, setTemplateDescription] = useState("");
    const [driverType, setDriverType] = useState<RevenueDriverType>("fixed");
    const [driverValue, setDriverValue] = useState("");
    const [notes, setNotes] = useState("");
    const [editingTemplateId, setEditingTemplateId] = useState("");
    const [editingTemplateName, setEditingTemplateName] = useState("");
    const [editingTemplateDescription, setEditingTemplateDescription] = useState("");
    const [editingTemplateDriverType, setEditingTemplateDriverType] = useState<RevenueDriverType>("fixed");
    const [editingTemplateDriverValue, setEditingTemplateDriverValue] = useState("");
    const [monthOverrides, setMonthOverrides] = useState<Record<string, string>>({});
    const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
    const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);
    const [archivingTemplateId, setArchivingTemplateId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isBulkApplying, setIsBulkApplying] = useState(false);
    const [mutationError, setMutationError] = useState<string | null>(null);

    const parentGroups = grid?.parent_groups ?? EMPTY_COST_PARENT_GROUPS;
    const actualMonthKeys = grid?.actual_months ?? EMPTY_MONTH_KEYS;
    const forecastMonthKeys = grid?.forecast_months ?? EMPTY_MONTH_KEYS;
    const scopeOptions = useMemo<CostScopeOption[]>(() => {
        const options: CostScopeOption[] = [];
        for (const parentGroup of parentGroups) {
            const pnlSection = parentGroup.pnl_section === "cogs" ? "cogs" : "expenses";
            options.push({
                key: costScopeOptionKey(pnlSection, "parent_group", parentGroup.label),
                pnlSection,
                scopeType: "parent_group",
                scopeId: parentGroup.label,
                scopeLabel: parentGroup.label,
                description: pnlSection === "cogs" ? "COGS parent group" : "OpEx parent group",
            });
            for (const accountGroup of parentGroup.account_groups) {
                options.push({
                    key: costScopeOptionKey(pnlSection, "account_group", accountGroup.label),
                    pnlSection,
                    scopeType: "account_group",
                    scopeId: accountGroup.label,
                    scopeLabel: accountGroup.label,
                    description: `${parentGroup.label} account group`,
                });
                for (const account of accountGroup.accounts) {
                    const accountLabel = account.code ? `${account.code} · ${account.name}` : account.name;
                    options.push({
                        key: costScopeOptionKey(pnlSection, "account", account.id),
                        pnlSection,
                        scopeType: "account",
                        scopeId: account.id,
                        scopeLabel: accountLabel,
                        description: `${accountGroup.label} account`,
                        accountId: account.id,
                    });
                    for (const supplier of account.suppliers) {
                        if (supplier.id === "_no_supplier") continue;
                        options.push({
                            key: costScopeOptionKey(pnlSection, "supplier", supplier.id, account.id),
                            pnlSection,
                            scopeType: "supplier",
                            scopeId: supplier.id,
                            scopeLabel: supplier.name,
                            description: `${accountLabel} supplier`,
                            accountId: account.id,
                        });
                    }
                }
            }
        }
        return options;
    }, [parentGroups]);
    const assumptionByScope = useMemo(() => {
        const map = new Map<string, CostAssumption>();
        for (const assumption of assumptions) {
            map.set(
                costAssumptionMapKey(
                    assumption.pnl_section,
                    assumption.scope_type,
                    assumption.scope_id,
                    assumption.account_id,
                ),
                assumption,
            );
        }
        return map;
    }, [assumptions]);
    const templateUsageCount = useMemo(() => {
        const usage = new Map<string, number>();
        for (const assumption of assumptions) {
            if (!assumption.template_id) continue;
            usage.set(assumption.template_id, (usage.get(assumption.template_id) ?? 0) + 1);
        }
        return usage;
    }, [assumptions]);
    const selectedScope = scopeOptions.find((option) => option.key === scopeKey) ?? null;
    const selectedAssumption = selectedScope
        ? assumptionByScope.get(
            costAssumptionMapKey(
                selectedScope.pnlSection,
                selectedScope.scopeType,
                selectedScope.scopeId,
                selectedScope.accountId,
            ),
        ) ?? null
        : null;
    const canBulkApply = selectedScope != null && selectedScope.scopeType !== "supplier";
    const selectedTemplate = templates.find((template) => template.id === templateId) ?? null;
    const editableTemplate = templates.find((template) => template.id === editingTemplateId) ?? null;

    useEffect(() => {
        if (!scopeOptions.length) {
            setScopeKey("");
            return;
        }
        setScopeKey((current) => current || scopeOptions[0].key);
    }, [scopeOptions]);

    useEffect(() => {
        if (requestedScopeKey && scopeOptions.some((option) => option.key === requestedScopeKey)) {
            setScopeKey(requestedScopeKey);
        }
    }, [requestedScopeKey, scopeOptions]);

    useEffect(() => {
        if (!templates.length) {
            setEditingTemplateId("");
            setEditingTemplateName("");
            setEditingTemplateDescription("");
            setEditingTemplateDriverType("fixed");
            setEditingTemplateDriverValue("");
            return;
        }
        setEditingTemplateId((current) => (
            current && templates.some((template) => template.id === current)
                ? current
                : templates[0].id
        ));
    }, [templates]);

    useEffect(() => {
        if (!selectedAssumption) {
            setAssumptionMode("direct");
            setTemplateId("");
            setTemplateName("");
            setTemplateDescription("");
            setDriverType("fixed");
            setDriverValue("");
            setNotes("");
            setMonthOverrides(Object.fromEntries(forecastMonthKeys.map((monthKey) => [monthKey, ""])));
            return;
        }
        setAssumptionMode(selectedAssumption.template_id ? "template" : "direct");
        setTemplateId(selectedAssumption.template_id ?? "");
        setTemplateName("");
        setTemplateDescription("");
        setDriverType((selectedAssumption.template_id
            ? selectedAssumption.effective_driver_type
            : (selectedAssumption.driver_type || selectedAssumption.effective_driver_type)
        ) as RevenueDriverType);
        setDriverValue(driverValueFromAssumption(selectedAssumption));
        setNotes(selectedAssumption.notes || "");
        setMonthOverrides(
            Object.fromEntries(
                forecastMonthKeys.map((monthKey) => [
                    monthKey,
                    selectedAssumption.month_overrides?.[monthKey] != null
                        ? String(selectedAssumption.month_overrides[monthKey])
                        : "",
                ]),
            ),
        );
    }, [forecastMonthKeys, selectedAssumption]);

    useEffect(() => {
        if (!editableTemplate) return;
        setEditingTemplateName(editableTemplate.name);
        setEditingTemplateDescription(editableTemplate.description || "");
        setEditingTemplateDriverType(editableTemplate.driver_type);
        setEditingTemplateDriverValue(driverValueFromTemplate(editableTemplate));
    }, [editableTemplate]);

    const refreshCostData = async () => Promise.all([refreshGrid(), refreshAssumptions(), onModelRefresh()]);

    const handleCreateTemplate = async () => {
        if (assumptionMode !== "direct") return;
        if (!templateName.trim()) {
            setMutationError("Template name is required.");
            return;
        }
        if (!hasValidDriverValue(driverType, driverValue)) {
            setMutationError(`${driverValueLabel(driverType, "cost")} is required.`);
            return;
        }

        setIsCreatingTemplate(true);
        setMutationError(null);
        try {
            const result = await createCostTemplate(workspaceId, {
                name: templateName.trim(),
                description: templateDescription.trim(),
                driver_type: driverType,
                params: buildRevenueParams(driverType, driverValue),
            });
            await refreshTemplates();
            const createdTemplateId = result?.data?.template?.id ?? "";
            if (createdTemplateId) {
                setTemplateId(createdTemplateId);
                setEditingTemplateId(createdTemplateId);
                setAssumptionMode("template");
            }
            setTemplateName("");
            setTemplateDescription("");
            toast.success("Cost template created");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to create cost template";
            setMutationError(message);
            toast.error(message);
        } finally {
            setIsCreatingTemplate(false);
        }
    };

    const handleUpdateTemplate = async () => {
        if (!editableTemplate) return;
        if (!editingTemplateName.trim()) {
            setMutationError("Template name is required.");
            return;
        }
        if (!hasValidDriverValue(editingTemplateDriverType, editingTemplateDriverValue)) {
            setMutationError(`${driverValueLabel(editingTemplateDriverType, "cost")} is required.`);
            return;
        }

        setIsUpdatingTemplate(true);
        setMutationError(null);
        try {
            await updateCostTemplate(workspaceId, editableTemplate.id, {
                name: editingTemplateName.trim(),
                description: editingTemplateDescription.trim(),
                driver_type: editingTemplateDriverType,
                params: buildRevenueParams(editingTemplateDriverType, editingTemplateDriverValue),
            });
            await refreshTemplates();
            toast.success("Cost template updated");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to update cost template";
            setMutationError(message);
            toast.error(message);
        } finally {
            setIsUpdatingTemplate(false);
        }
    };

    const handleArchiveTemplate = async (template: CostForecastTemplate) => {
        const activeUsageCount = templateUsageCount.get(template.id) ?? 0;
        if (activeUsageCount > 0) {
            setMutationError(`Template "${template.name}" is still used by ${activeUsageCount} active assumption${activeUsageCount === 1 ? "" : "s"}.`);
            return;
        }
        if (!window.confirm(`Archive template "${template.name}"?`)) return;

        setArchivingTemplateId(template.id);
        setMutationError(null);
        try {
            await archiveCostTemplate(workspaceId, template.id);
            if (templateId === template.id) setTemplateId("");
            await refreshTemplates();
            toast.success("Cost template archived");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to archive cost template";
            setMutationError(message);
            toast.error(message);
        } finally {
            setArchivingTemplateId(null);
        }
    };

    const handleSaveAssumption = async () => {
        if (!selectedScope) return;
        if (assumptionMode === "template" && !templateId) {
            setMutationError("Template is required.");
            return;
        }
        if (assumptionMode === "direct" && !hasValidDriverValue(driverType, driverValue)) {
            setMutationError(`${driverValueLabel(driverType, "cost")} is required.`);
            return;
        }

        setIsSaving(true);
        setMutationError(null);
        try {
            const overrides = buildMonthOverrides(monthOverrides);
            if (selectedAssumption) {
                if (assumptionMode === "template") {
                    await updateCostAssumption(workspaceId, selectedAssumption.id, {
                        template_id: templateId,
                        month_overrides: overrides,
                        notes,
                    });
                } else {
                    await updateCostAssumption(workspaceId, selectedAssumption.id, {
                        driver_type: driverType,
                        params: buildRevenueParams(driverType, driverValue),
                        month_overrides: overrides,
                        notes,
                        template_id: null,
                    });
                }
            } else {
                await saveCostAssumption(workspaceId, {
                    scenario_id: scenarioId,
                    pnl_section: selectedScope.pnlSection,
                    scope_type: selectedScope.scopeType,
                    scope_id: selectedScope.scopeId,
                    scope_label: selectedScope.scopeLabel,
                    account_id: selectedScope.accountId,
                    template_id: assumptionMode === "template" ? templateId : undefined,
                    driver_type: assumptionMode === "direct" ? driverType : undefined,
                    params: assumptionMode === "direct" ? buildRevenueParams(driverType, driverValue) : undefined,
                    month_overrides: overrides,
                    notes,
                });
            }
            await refreshCostData();
            toast.success(selectedAssumption ? "Cost assumption updated" : "Cost assumption created");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to save cost assumption";
            setMutationError(message);
            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteAssumption = async (assumption: CostAssumption) => {
        setDeletingId(assumption.id);
        setMutationError(null);
        try {
            await deleteCostAssumption(workspaceId, assumption.id);
            await refreshCostData();
            toast.success("Cost assumption removed");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to delete cost assumption";
            setMutationError(message);
            toast.error(message);
        } finally {
            setDeletingId(null);
        }
    };

    const handleBulkApply = async () => {
        if (!selectedScope || !canBulkApply) return;
        if (assumptionMode === "template" && !templateId) {
            setMutationError("Template is required.");
            return;
        }
        if (assumptionMode === "direct" && !hasValidDriverValue(driverType, driverValue)) {
            setMutationError(`${driverValueLabel(driverType, "cost")} is required.`);
            return;
        }

        setIsBulkApplying(true);
        setMutationError(null);
        try {
            const result = await costBulkApply(workspaceId, {
                scenario_id: scenarioId,
                pnl_section: selectedScope.pnlSection,
                scope_type: selectedScope.scopeType as "parent_group" | "account_group" | "account",
                scope_id: selectedScope.scopeId,
                driver_type: assumptionMode === "direct" ? driverType : undefined,
                template_id: assumptionMode === "template" ? templateId : undefined,
                params: assumptionMode === "direct" ? buildRevenueParams(driverType, driverValue) : undefined,
                month_overrides: buildMonthOverrides(monthOverrides),
                notes: notes || `Bulk applied from ${selectedScope.scopeLabel}`,
            });
            await refreshCostData();
            const data = result?.data;
            const affectedCount = Number(data?.created_count ?? 0) + Number(data?.updated_count ?? 0);
            toast.success(`Bulk-applied ${affectedCount} cost assumption${affectedCount === 1 ? "" : "s"}`);
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to bulk apply cost assumptions";
            setMutationError(message);
            toast.error(message);
        } finally {
            setIsBulkApplying(false);
        }
    };

    return (
        <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[2px] text-amber-700">Phase 7 | Cost Planning</p>
                    <h2 className="mt-1 text-lg font-semibold text-slate-900">Bottom-up COGS and OpEx inside Workbench</h2>
                    <p className="mt-1 text-sm text-slate-500">Supplier, account, account-group, and parent-group assumptions feed the same model engine as the P&L grid.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                        {(assumptionsLoading ? "…" : assumptions.length)} active assumptions
                    </Badge>
                    <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50">
                        {forecastMonthKeys.length} forecast months
                    </Badge>
                </div>
            </div>

            <div className="mt-4 space-y-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <p className="text-sm font-medium text-slate-900">Edit cost assumptions</p>
                            <p className="mt-1 text-sm text-slate-500">Select a COGS/OpEx scope, attach a driver or template, and refresh the workbench model.</p>
                        </div>
                        <Badge className="bg-white text-slate-700 hover:bg-white">
                            {templates.length} templates available
                        </Badge>
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <div className="space-y-2">
                            <Label htmlFor="cost-scope">Scope</Label>
                            <Select value={scopeKey} onValueChange={setScopeKey} disabled={isLoading || scopeOptions.length === 0}>
                                <SelectTrigger id="cost-scope" className="bg-white">
                                    <SelectValue placeholder={isLoading ? "Loading…" : scopeOptions.length === 0 && !error ? "No cost map configured" : "Select scope"} />
                                </SelectTrigger>
                                <SelectContent>
                                    {scopeOptions.map((option) => (
                                        <SelectItem key={option.key} value={option.key}>
                                            {option.scopeLabel} · {option.description}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="cost-assumption-mode">Input mode</Label>
                            <Select value={assumptionMode} onValueChange={(value) => setAssumptionMode(value as CostAssumptionMode)}>
                                <SelectTrigger id="cost-assumption-mode" className="bg-white">
                                    <SelectValue placeholder="Select mode" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="direct">Direct driver</SelectItem>
                                    <SelectItem value="template">Saved template</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="cost-template">Template</Label>
                            <Select value={templateId} onValueChange={setTemplateId} disabled={assumptionMode !== "template"}>
                                <SelectTrigger id="cost-template" className="bg-white">
                                    <SelectValue placeholder={templates.length ? "Select template" : "No templates available"} />
                                </SelectTrigger>
                                <SelectContent>
                                    {templates.map((template) => (
                                        <SelectItem key={template.id} value={template.id}>
                                            {template.name} · {template.driver_type}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="cost-driver">Driver</Label>
                            <Select
                                value={driverType}
                                onValueChange={(value) => setDriverType(value as RevenueDriverType)}
                                disabled={assumptionMode === "template"}
                            >
                                <SelectTrigger id="cost-driver" className="bg-white">
                                    <SelectValue placeholder="Select driver" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="fixed">Fixed</SelectItem>
                                    <SelectItem value="growth">Growth</SelectItem>
                                    <SelectItem value="flat">Flat</SelectItem>
                                    <SelectItem value="last_year_curve">Last Year Curve</SelectItem>
                                    <SelectItem value="seasonality">Seasonality</SelectItem>
                                    <SelectItem value="step">Step</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="cost-driver-value">{driverValueLabel(driverType, "cost")}</Label>
                            <Input
                                id="cost-driver-value"
                                value={driverValue}
                                onChange={(event) => setDriverValue(event.target.value)}
                                disabled={assumptionMode === "template" || driverType === "flat"}
                                placeholder={driverType === "growth" ? "10" : driverType === "fixed" ? "25000" : driverType === "last_year_curve" ? "5" : "Value"}
                                className="bg-white"
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2 xl:col-span-3">
                            <Label htmlFor="cost-notes">Notes</Label>
                            <Input
                                id="cost-notes"
                                value={notes}
                                onChange={(event) => setNotes(event.target.value)}
                                placeholder="Optional rationale"
                                className="bg-white"
                            />
                        </div>
                    </div>
                    <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
                        <p className="text-sm font-medium text-slate-900">Month overrides</p>
                        <p className="mt-1 text-sm text-slate-500">Optional forecast-month overrides on top of the selected driver or template.</p>
                        <div className="mt-3 grid gap-3 md:grid-cols-3 xl:grid-cols-6">
                            {forecastMonthKeys.map((monthKey) => (
                                <div key={monthKey} className="space-y-2">
                                    <Label htmlFor={`cost-override-${monthKey}`}>{monthKey}</Label>
                                    <Input
                                        id={`cost-override-${monthKey}`}
                                        value={monthOverrides[monthKey] ?? ""}
                                        onChange={(event) => setMonthOverrides((current) => ({
                                            ...current,
                                            [monthKey]: event.target.value,
                                        }))}
                                        placeholder="Optional override"
                                        className="bg-white"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Button onClick={handleSaveAssumption} disabled={isSaving || isBulkApplying || !selectedScope}>
                            {isSaving ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                            {selectedAssumption ? "Update cost assumption" : "Create cost assumption"}
                        </Button>
                        {canBulkApply ? (
                            <Button variant="outline" onClick={handleBulkApply} disabled={isSaving || isBulkApplying}>
                                {isBulkApplying ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                                Bulk apply to child cost rows
                            </Button>
                        ) : null}
                        {selectedAssumption ? (
                            <Button
                                variant="outline"
                                disabled={deletingId === selectedAssumption.id || isSaving || isBulkApplying}
                                onClick={() => handleDeleteAssumption(selectedAssumption)}
                            >
                                {deletingId === selectedAssumption.id ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                                Remove assumption
                            </Button>
                        ) : null}
                        {selectedScope ? (
                            <span className="text-sm text-slate-500">
                                Editing {selectedScope.scopeLabel}
                                {selectedAssumption
                                    ? selectedAssumption.template_id
                                        ? ` · current template ${selectedAssumption.template_id}`
                                        : ` · current driver ${driverType}`
                                    : " · no active assumption yet"}
                            </span>
                        ) : null}
                    </div>
                    {assumptionMode === "template" && selectedTemplate ? (
                        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                            Using template <span className="font-medium">{selectedTemplate.name}</span> · {selectedTemplate.driver_type}
                        </div>
                    ) : null}
                    {mutationError ? (
                        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                            {mutationError}
                        </div>
                    ) : null}
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <p className="text-sm font-medium text-slate-900">Save current cost driver as template</p>
                        <p className="mt-1 text-sm text-slate-500">Templates are reusable across COGS and OpEx scopes.</p>
                        <div className="mt-3 grid gap-3 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="cost-template-name">Template name</Label>
                                <Input
                                    id="cost-template-name"
                                    value={templateName}
                                    onChange={(event) => setTemplateName(event.target.value)}
                                    placeholder="e.g. Payroll growth"
                                    className="bg-white"
                                    disabled={assumptionMode !== "direct"}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="cost-template-description">Description</Label>
                                <Input
                                    id="cost-template-description"
                                    value={templateDescription}
                                    onChange={(event) => setTemplateDescription(event.target.value)}
                                    placeholder="Optional notes for reuse"
                                    className="bg-white"
                                    disabled={assumptionMode !== "direct"}
                                />
                            </div>
                        </div>
                        <Button
                            className="mt-3"
                            variant="outline"
                            onClick={handleCreateTemplate}
                            disabled={assumptionMode !== "direct" || isCreatingTemplate || isSaving}
                        >
                            {isCreatingTemplate ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                            Save as template
                        </Button>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <p className="text-sm font-medium text-slate-900">Manage cost templates</p>
                                <p className="mt-1 text-sm text-slate-500">Edit active reusable drivers or archive unused ones.</p>
                            </div>
                            <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">{templates.length} active</Badge>
                        </div>
                        {templates.length ? (
                            <div className="mt-3 grid gap-3 md:grid-cols-2">
                                <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="cost-template-editor">Template</Label>
                                    <Select value={editingTemplateId} onValueChange={setEditingTemplateId}>
                                        <SelectTrigger id="cost-template-editor" className="bg-white">
                                            <SelectValue placeholder="Select template" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {templates.map((template) => (
                                                <SelectItem key={template.id} value={template.id}>
                                                    {template.name} · {template.driver_type}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="cost-template-editor-name">Name</Label>
                                    <Input
                                        id="cost-template-editor-name"
                                        value={editingTemplateName}
                                        onChange={(event) => setEditingTemplateName(event.target.value)}
                                        className="bg-white"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="cost-template-editor-driver">Driver</Label>
                                    <Select
                                        value={editingTemplateDriverType}
                                        onValueChange={(value) => setEditingTemplateDriverType(value as RevenueDriverType)}
                                    >
                                        <SelectTrigger id="cost-template-editor-driver" className="bg-white">
                                            <SelectValue placeholder="Select driver" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="fixed">Fixed</SelectItem>
                                            <SelectItem value="growth">Growth</SelectItem>
                                            <SelectItem value="flat">Flat</SelectItem>
                                            <SelectItem value="last_year_curve">Last Year Curve</SelectItem>
                                            <SelectItem value="seasonality">Seasonality</SelectItem>
                                            <SelectItem value="step">Step</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="cost-template-editor-value">{driverValueLabel(editingTemplateDriverType, "cost")}</Label>
                                    <Input
                                        id="cost-template-editor-value"
                                        value={editingTemplateDriverValue}
                                        onChange={(event) => setEditingTemplateDriverValue(event.target.value)}
                                        disabled={editingTemplateDriverType === "flat"}
                                        className="bg-white"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="cost-template-editor-description">Description</Label>
                                    <Input
                                        id="cost-template-editor-description"
                                        value={editingTemplateDescription}
                                        onChange={(event) => setEditingTemplateDescription(event.target.value)}
                                        className="bg-white"
                                    />
                                </div>
                                <div className="flex flex-wrap items-center gap-2 md:col-span-2">
                                    <Button variant="outline" onClick={handleUpdateTemplate} disabled={isUpdatingTemplate || !editableTemplate}>
                                        {isUpdatingTemplate ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                                        Update template
                                    </Button>
                                    {editableTemplate ? (
                                        <Button
                                            variant="outline"
                                            disabled={archivingTemplateId === editableTemplate.id || (templateUsageCount.get(editableTemplate.id) ?? 0) > 0}
                                            onClick={() => handleArchiveTemplate(editableTemplate)}
                                        >
                                            {archivingTemplateId === editableTemplate.id ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                                            Archive template
                                        </Button>
                                    ) : null}
                                    {editableTemplate ? (
                                        <span className="text-sm text-slate-500">
                                            {(templateUsageCount.get(editableTemplate.id) ?? 0) > 0
                                                ? `Used by ${templateUsageCount.get(editableTemplate.id) ?? 0} active assumption${(templateUsageCount.get(editableTemplate.id) ?? 0) === 1 ? "" : "s"}`
                                                : "Unused template"}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        ) : (
                            <div className="mt-3 rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                                No active cost templates yet.
                            </div>
                        )}
                    </div>
                </div>

                {isLoading && <Skeleton className="h-40 w-full rounded-2xl" />}
                {error && <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}
                {!isLoading && !error && grid && (
                    <>
                        <div className="grid gap-3 md:grid-cols-3">
                            <Card className="border-slate-200 shadow-none">
                                <CardContent className="p-4">
                                    <p className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-500">Actual Costs</p>
                                    <p className="mt-2 text-xl font-semibold text-slate-950">
                                        {formatMetricValue(sumMonthValues(grid.grand_total, actualMonthKeys), "currency")}
                                    </p>
                                </CardContent>
                            </Card>
                            <Card className="border-slate-200 shadow-none">
                                <CardContent className="p-4">
                                    <p className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-500">Forecast Costs</p>
                                    <p className="mt-2 text-xl font-semibold text-slate-950">
                                        {formatMetricValue(sumMonthValues(grid.grand_total, forecastMonthKeys), "currency")}
                                    </p>
                                </CardContent>
                            </Card>
                            <Card className="border-slate-200 shadow-none">
                                <CardContent className="p-4">
                                    <p className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-500">Coverage</p>
                                    <p className="mt-2 text-xl font-semibold text-slate-950">
                                        {parentGroups.length} parent groups
                                    </p>
                                </CardContent>
                            </Card>
                        </div>

                        <div className="rounded-xl border border-slate-200">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Scope</TableHead>
                                        <TableHead>Driver</TableHead>
                                        <TableHead>Value</TableHead>
                                        <TableHead>Notes</TableHead>
                                        <TableHead className="text-right">Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {assumptions.map((assumption) => (
                                        <TableRow key={assumption.id}>
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium text-slate-900">{assumption.scope_label || assumption.scope_id}</p>
                                                    <p className="text-xs text-slate-500">{assumption.pnl_section} · {assumption.scope_type}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>{assumption.template_id ? assumption.effective_driver_type : assumption.driver_type || assumption.effective_driver_type}</TableCell>
                                            <TableCell>{assumption.template_id ? "Template" : (driverValueFromAssumption(assumption) || "—")}</TableCell>
                                            <TableCell className="max-w-[280px] truncate text-sm text-slate-500">{assumption.notes || "—"}</TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => {
                                                            const matchingScope = scopeOptions.find((option) =>
                                                                option.pnlSection === assumption.pnl_section
                                                                && option.scopeType === assumption.scope_type
                                                                && option.scopeId === assumption.scope_id
                                                                && (assumption.scope_type !== "supplier" || option.accountId === assumption.account_id),
                                                            );
                                                            if (matchingScope) setScopeKey(matchingScope.key);
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        disabled={deletingId === assumption.id}
                                                        onClick={() => handleDeleteAssumption(assumption)}
                                                    >
                                                        {deletingId === assumption.id ? <Loader2Icon className="mr-2 size-3.5 animate-spin" /> : null}
                                                        Remove
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {assumptions.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} className="py-8 text-center text-sm text-slate-500">
                                                No active cost assumptions saved yet.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}

const OP_TYPE_NARRATIVES: Record<string, string> = {
    set_baseline: "Set baseline method",
    set_driver: "Applied forecast driver",
    set_override: "Manual override",
    set_weekly_ovr: "Weekly manual adjustment",
};

const DRIVER_TYPE_NARRATIVES: Record<string, string> = {
    growth: "growth rate",
    fixed: "fixed value",
    ratio: "ratio of revenue",
    allocation: "cost allocation",
    step_change: "step change",
    scale: "proportional scaling",
};

function formatOpNarrative(op: ModelingWorkbenchOpHistoryItem, nodeLabels: Map<string, string>): string {
    const target = op.target_node_id
        ? (nodeLabels.get(op.target_node_id) ?? op.target_node_id)
        : "model";
    const params = op.params ?? {};

    if (op.op_type === "set_driver") {
        const driverType = String(params.driver_type ?? params.type ?? "");
        const driverLabel = DRIVER_TYPE_NARRATIVES[driverType] ?? driverType;
        const value = params.value ?? params.rate ?? params.growth_rate;
        const from = op.target_month ?? "";

        if (driverType === "scale" && typeof value === "number") {
            const pctChange = ((value - 1) * 100).toFixed(2);
            return `Scaled ${target} by ${Number(pctChange) > 0 ? "+" : ""}${pctChange}%${from ? ` from ${from} onward` : ""}. ` +
                `This applies a flat ${driverLabel} factor to the baseline forecast for this category.`;
        }
        if (driverType === "growth" && typeof value === "number") {
            const pctGrowth = (value * 100).toFixed(1);
            return `Applied ${pctGrowth}% monthly ${driverLabel} to ${target}${from ? ` from ${from}` : ""}. ` +
                `Compounds on the baseline value each subsequent period.`;
        }
        if (driverType === "fixed" && value != null) {
            return `Set ${target} to a fixed value of $${Number(value).toLocaleString()}${from ? ` from ${from}` : ""}.`;
        }
        if (driverType === "step_change" && typeof value === "number") {
            const pctStep = (value * 100).toFixed(1);
            return `Applied a ${pctStep}% step change to ${target}${from ? ` effective ${from}` : ""}. ` +
                `A one-time level shift applied to the baseline.`;
        }
        return `Applied ${driverLabel} driver to ${target}${from ? ` from ${from}` : ""}${value != null ? ` (value: ${value})` : ""}.`;
    }

    if (op.op_type === "set_override") {
        const val = params.value;
        return `Manually set ${target} to $${Number(val).toLocaleString()} for ${op.target_month ?? "target period"}. ` +
            `This overrides both the baseline and any active drivers for this cell.`;
    }

    if (op.op_type === "set_baseline") {
        const method = String(params.method ?? "");
        return `Changed baseline method for ${target} to "${method.replace(/_/g, " ")}".`;
    }

    return op.label || `${OP_TYPE_NARRATIVES[op.op_type] ?? op.op_type} on ${target}.`;
}

function buildBaselineCommentary(baselineMethod: string, kpis: ModelingWorkbenchKpi[]): string {
    const methodMap: Record<string, string> = {
        avg_3m: "3-month trailing average",
        avg_6m: "6-month trailing average",
        avg_12m: "12-month trailing average",
        last_month: "last reported month",
        yoy_flat: "year-on-year (flat)",
        yoy_growth: "year-on-year with growth trend",
    };
    const methodLabel = methodMap[baselineMethod] ?? baselineMethod.replace(/_/g, " ");
    const hasKpis = kpis.some((k) => k.value != null);
    const parts = [
        `Baseline forecast uses the "${methodLabel}" method. `,
        "Each P&L line item is projected forward using this method applied to its historical actuals. ",
        "Lines with zero activity in the trailing window are excluded from the forecast to avoid noise.",
    ];
    if (hasKpis) {
        const rev = kpis.find((k) => k.key === "revenue");
        const np = kpis.find((k) => k.key === "net_profit");
        if (rev?.value != null && np?.value != null) {
            parts.push(
                ` Under baseline assumptions, forecast revenue is $${rev.value.toLocaleString(undefined, {maximumFractionDigits: 0})}` +
                ` with net profit of $${np.value.toLocaleString(undefined, {maximumFractionDigits: 0})}.`,
            );
        }
    }
    return parts.join("");
}

function buildScenarioCommentary(
    scenarioName: string,
    activeOps: ModelingWorkbenchOpHistoryItem[],
    nodeLabels: Map<string, string>,
): string {
    if (activeOps.length === 0) {
        return `${scenarioName} has no active scenario adjustments. The forecast is currently tracking the baseline assumptions.`;
    }

    const sampleNarratives = activeOps
        .slice(0, 3)
        .map((op) => formatOpNarrative(op, nodeLabels))
        .filter(Boolean);

    const opener = `${scenarioName} applies ${activeOps.length} active ${activeOps.length === 1 ? "adjustment" : "adjustments"} on top of the baseline forecast.`;
    if (sampleNarratives.length === 0) return opener;

    const detail = sampleNarratives.join(" ");
    const moreCount = activeOps.length - sampleNarratives.length;
    const suffix = moreCount > 0 ? ` ${moreCount} additional adjustment${moreCount === 1 ? "" : "s"} remain active.` : "";
    return `${opener} ${detail}${suffix}`;
}

function AssumptionsRegisterSheet({
    open,
    onOpenChange,
    opsHistory,
    nodeLabels,
    scenarioName,
    baselineMethod,
    kpis,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    opsHistory: ModelingWorkbenchOpHistoryItem[];
    nodeLabels: Map<string, string>;
    scenarioName: string;
    baselineMethod: string;
    kpis: ModelingWorkbenchKpi[];
}) {
    const activeOps = useMemo(() => opsHistory.filter((op) => op.is_active), [opsHistory]);
    const isBaselineScenario = scenarioName.trim().toLowerCase() === "base";
    const scenarioCommentary = useMemo(
        () => buildScenarioCommentary(scenarioName, activeOps, nodeLabels),
        [scenarioName, activeOps, nodeLabels],
    );

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" data-tour="workbench-assumptions-drawer">
                <SheetHeader>
                    <SheetTitle>Scenario Assumptions</SheetTitle>
                    <SheetDescription>
                        Key modelling assumptions and commentary for this scenario.
                    </SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                    {!isBaselineScenario && (
                        <div className="rounded-xl border border-slate-200 bg-white p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50">{scenarioName}</Badge>
                                <span className="text-xs font-medium text-slate-600">Active scenario</span>
                            </div>
                            <p className="text-sm leading-relaxed text-slate-700">
                                {scenarioCommentary}
                            </p>
                        </div>
                    )}

                    {/* Baseline commentary */}
                    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-sky-50 text-sky-700 hover:bg-sky-50">Baseline</Badge>
                            <span className="text-xs font-medium text-slate-600">
                                {isBaselineScenario ? scenarioName : "Reference forecast"}
                            </span>
                        </div>
                        <p className="text-sm leading-relaxed text-slate-700">
                            {buildBaselineCommentary(baselineMethod, kpis)}
                        </p>
                    </div>

                    {/* Active scenario assumptions */}
                    {activeOps.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-slate-300 p-5 text-center">
                            <p className="text-sm text-slate-500">No active scenario adjustments.</p>
                            <p className="mt-1 text-xs text-slate-400">This is the baseline forecast with no modifications applied.</p>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-2">
                                <p className="text-xs font-semibold uppercase tracking-[1.5px] text-slate-500">
                                    Scenario Adjustments
                                </p>
                                <Badge variant="outline" className="text-[10px]">{activeOps.length}</Badge>
                            </div>
                            <div className="space-y-3">
                                {activeOps.map((op) => (
                                    <div key={op.id} className="rounded-xl border border-slate-200 bg-white p-4">
                                        <div className="flex items-start justify-between gap-2 mb-1.5">
                                            <p className="text-xs font-semibold text-slate-900">
                                                {op.label || OP_TYPE_NARRATIVES[op.op_type] || op.op_type}
                                            </p>
                                            <Badge className={cn(
                                                "text-[9px] uppercase",
                                                op.op_type === "set_driver" && "bg-violet-50 text-violet-700 hover:bg-violet-50",
                                                op.op_type === "set_override" && "bg-sky-50 text-sky-700 hover:bg-sky-50",
                                                op.op_type === "set_baseline" && "bg-slate-100 text-slate-600 hover:bg-slate-100",
                                            )}>
                                                {op.op_type === "set_driver" ? "driver" : op.op_type === "set_override" ? "override" : "baseline"}
                                            </Badge>
                                        </div>
                                        <p className="text-sm leading-relaxed text-slate-600">
                                            {formatOpNarrative(op, nodeLabels)}
                                        </p>
                                        {op.target_month && (
                                            <p className="mt-1.5 text-[10px] text-slate-400">
                                                Effective: {op.target_month} onward
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}

function WorkbenchForecastOverridePopover({
    workspaceId,
    scenarioId,
    months,
    actualMonths,
    baselineMethod,
    cell,
    cellValue,
    scope,
    opsHistory,
    rowIsBold,
    categoryKey,
    onCommitted,
    onForecastDriverSaved,
    onOpenDetail,
}: {
    workspaceId: string;
    scenarioId: string;
    months: number;
    actualMonths: number;
    baselineMethod: string;
    cell: SelectedWorkbenchCell;
    cellValue: ModelingWorkbenchCellValue;
    scope: ForecastAssumptionScope;
    opsHistory: ModelingWorkbenchOpHistoryItem[];
    rowIsBold: boolean;
    categoryKey: string;
    onCommitted: (data: ModelingWorkbenchData) => Promise<void>;
    onForecastDriverSaved: () => Promise<void>;
    onOpenDetail: (cell: SelectedWorkbenchCell) => void;
}) {
    const matchingRevisionOps = useMemo(
        () => opsHistory
            .filter((op) => isBottomUpOverrideOpForScope(op, scope, cell.monthKey))
            .sort((a, b) => b.id.localeCompare(a.id)),
        [opsHistory, scope, cell.monthKey],
    );
    const activeOverrideOps = useMemo(
        () => matchingRevisionOps.filter((op) => op.is_active).slice().reverse(),
        [matchingRevisionOps],
    );
    const hasActiveOverride = cellValue.is_override || activeOverrideOps.length > 0;
    const activeOverrideValue = activeOverrideOps.at(-1)?.params?.value;
    const [open, setOpen] = useState(false);
    const [draftValue, setDraftValue] = useState("");
    const [note, setNote] = useState("");
    const [mutationError, setMutationError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isClearing, setIsClearing] = useState(false);

    useEffect(() => {
        if (!open) return;
        setDraftValue(
            typeof activeOverrideValue === "number" || typeof activeOverrideValue === "string"
                ? String(activeOverrideValue)
                : String(cell.value),
        );
        setNote("");
        setMutationError(null);
    }, [open, activeOverrideValue, cell.value]);

    const handleSaveExactOverride = async (event?: FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        const parsed = parseOverrideValue(draftValue);
        if (!Number.isFinite(parsed)) {
            setMutationError("Enter a valid number.");
            return;
        }

        setIsSaving(true);
        setMutationError(null);
        try {
            const updated = await commitVNextModelingWorkbenchBottomUpOverride(workspaceId, {
                scenario_id: scenarioId,
                scope: bottomUpOverrideScopeForAssumptionScope(scope),
                month_key: cell.monthKey,
                value: parsed,
                note,
                n_months: months,
                n_actual_months: actualMonths,
            });
            await onCommitted(updated);
            await onForecastDriverSaved();
            setOpen(false);
            toast.success("Exact override saved");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to save exact override";
            setMutationError(message);
            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleClearExactOverride = async () => {
        setIsClearing(true);
        setMutationError(null);
        try {
            const updated = await commitVNextModelingWorkbenchBottomUpOverride(workspaceId, {
                scenario_id: scenarioId,
                scope: bottomUpOverrideScopeForAssumptionScope(scope),
                month_key: cell.monthKey,
                value: null,
                clear: true,
                note,
                n_months: months,
                n_actual_months: actualMonths,
            });
            await onCommitted(updated);
            await onForecastDriverSaved();
            setOpen(false);
            toast.success("Exact override cleared");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to clear exact override";
            setMutationError(message);
            toast.error(message);
        } finally {
            setIsClearing(false);
        }
    };

    const handleOpenDetail = () => {
        setOpen(false);
        onOpenDetail(cell);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-tour="workbench-forecast-override-trigger"
                    aria-label={`Inspect and override ${cell.nodeLabel} ${cell.monthLabel}`}
                    title="Click to inspect formula and override this forecast cell."
                    className="flex w-full items-center justify-end gap-1 rounded px-1 py-0.5 text-right transition-colors hover:bg-sky-50"
                >
                    {cellValue.has_ops && !cellValue.is_override && <span className="size-2 rounded-full bg-sky-500" />}
                    {cellValue.is_override && <span className="size-2 rounded-full bg-blue-500" />}
                    <span className={cn("text-slate-900", rowIsBold && "font-semibold", cellValue.is_override && "text-blue-700")}>
                        {formatCellValue(cellValue.value, categoryKey)}
                    </span>
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" side="top" className="w-[360px] border-slate-200 p-0 shadow-xl" data-tour="workbench-forecast-override-popover">
                <form onSubmit={handleSaveExactOverride}>
                    <div className="border-b border-slate-200 px-4 py-3">
                        <p className="text-sm font-semibold text-[#209DB3]">{cell.nodeLabel} — {cell.monthLabel}</p>
                        <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-500">
                            {scope.kind === "revenue" ? scope.scopeType : `${scope.pnlSection} ${scope.scopeType.replace("_", " ")}`} forecast cell
                        </p>
                    </div>
                    <div className="space-y-3 px-4 py-3">
                        <div className="space-y-1.5">
                            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Value ($)</Label>
                            <Input
                                value={draftValue}
                                onChange={(event) => setDraftValue(event.target.value)}
                                aria-label={`${cell.monthLabel} exact override`}
                                inputMode="decimal"
                                className="h-9 bg-white"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Note (optional)</Label>
                            <Input
                                value={note}
                                onChange={(event) => setNote(event.target.value)}
                                aria-label="Override note"
                                placeholder="e.g. Sean confirmed timing"
                                className="h-9 bg-white"
                            />
                        </div>
                        {mutationError && <p className="text-xs text-rose-600">{mutationError}</p>}
                        <div className="grid grid-cols-2 gap-2">
                            <Button type="submit" className="h-9 bg-[#209DB3] hover:bg-[#1a7d8f]" disabled={isSaving || isClearing}>
                                {isSaving ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                                Save
                            </Button>
                            <Button type="button" variant="outline" className="h-9" disabled={isSaving || isClearing} onClick={() => setOpen(false)}>
                                Cancel
                            </Button>
                        </div>
                        {hasActiveOverride && (
                            <Button type="button" variant="outline" className="h-8 w-full text-xs" disabled={isSaving || isClearing} onClick={handleClearExactOverride}>
                                {isClearing ? <Loader2Icon className="mr-2 size-3.5 animate-spin" /> : null}
                                Clear active override
                            </Button>
                        )}
                    </div>
                    <div className="space-y-3 border-t border-slate-200 bg-slate-50/70 px-4 py-3 text-xs">
                        <div>
                            <p className="font-semibold uppercase tracking-wide text-slate-500">Formula</p>
                            <p className="mt-1 font-mono text-[11px] leading-relaxed text-slate-700">{bottomUpFormulaSummary(scope, cell, hasActiveOverride, activeOverrideValue, baselineMethod)}</p>
                        </div>
                        <div>
                            <p className="font-semibold uppercase tracking-wide text-slate-500">Ops &amp; Revisions</p>
                            {matchingRevisionOps.length === 0 ? (
                                <p className="mt-1 text-slate-600">No override revisions recorded for this cell.</p>
                            ) : (
                                <div className="mt-1 space-y-1">
                                    {matchingRevisionOps.slice(0, 3).map((op) => (
                                        <div key={op.id} className="rounded border border-blue-100 bg-white px-2 py-1.5">
                                            <div className="flex items-center justify-between gap-2">
                                                <p className="font-medium text-blue-700">{op.label || "Exact override"}</p>
                                                <span className={cn(
                                                    "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                                                    op.is_active ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-500",
                                                )}>
                                                    {op.is_active ? "active" : "reverted"}
                                                </span>
                                            </div>
                                            <p className="text-slate-500">Value: {formatMetricValue(Number(op.params?.value ?? cell.value), "currency")}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <Button type="button" variant="ghost" className="h-8 w-full text-xs text-[#209DB3]" onClick={handleOpenDetail}>
                            Open assumptions &amp; calculation
                        </Button>
                    </div>
                </form>
            </PopoverContent>
        </Popover>
    );
}

function ForecastDriverEditor({
    workspaceId,
    scenarioId,
    months,
    actualMonths,
    baselineMethod,
    cell,
    scope,
    forecastMonthKeys,
    onSaved,
    onOverrideCommitted,
}: {
    workspaceId: string;
    scenarioId: string;
    months: number;
    actualMonths: number;
    baselineMethod: string;
    cell: SelectedWorkbenchCell;
    scope: ForecastAssumptionScope;
    forecastMonthKeys: string[];
    onSaved: () => Promise<void>;
    onOverrideCommitted: (data: ModelingWorkbenchData) => Promise<void>;
}) {
    const {assumptions: revenueAssumptions, refresh: refreshRevenueAssumptions} = useCustomerAssumptions(
        scope.kind === "revenue" ? workspaceId : null,
        scenarioId,
    );
    const {templates: revenueTemplates} = useRevenueTemplates(scope.kind === "revenue" ? workspaceId : null);
    const {assumptions: costAssumptions, refresh: refreshCostAssumptions} = useCostAssumptions(
        scope.kind === "cost" ? workspaceId : null,
        scenarioId,
    );
    const {templates: costTemplates} = useCostTemplates(scope.kind === "cost" ? workspaceId : null);
    const [assumptionMode, setAssumptionMode] = useState<RevenueAssumptionMode>("direct");
    const [templateId, setTemplateId] = useState("");
    const [driverType, setDriverType] = useState<RevenueDriverType>(scope.kind === "cost" ? "fixed" : "growth");
    const [driverValue, setDriverValue] = useState("");
    const [monthOverrideValue, setMonthOverrideValue] = useState("");
    const [applyOverrideForward, setApplyOverrideForward] = useState(false);
    const [notes, setNotes] = useState("");
    const [mutationError, setMutationError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingOverride, setIsSavingOverride] = useState(false);
    const [isClearingOverride, setIsClearingOverride] = useState(false);
    const [isBulkApplying, setIsBulkApplying] = useState(false);

    const selectedRevenueAssumption = scope.kind === "revenue"
        ? revenueAssumptions.find((assumption) => (
            assumption.scope_type === scope.scopeType
            && assumption.scope_id === scope.scopeId
        )) ?? null
        : null;
    const selectedCostAssumption = scope.kind === "cost"
        ? costAssumptions.find((assumption) => (
            costAssumptionMapKey(
                assumption.pnl_section,
                assumption.scope_type,
                assumption.scope_id,
                assumption.account_id,
            ) === costAssumptionMapKey(
                scope.pnlSection,
                scope.scopeType,
                scope.scopeId,
                scope.accountId,
            )
        )) ?? null
        : null;
    const selectedAssumption = selectedRevenueAssumption ?? selectedCostAssumption;
    const templates = scope.kind === "revenue" ? revenueTemplates : costTemplates;
    const scopeDescription = scope.kind === "revenue"
        ? `${scope.scopeLabel} · ${scope.scopeType}`
        : `${scope.scopeLabel} · ${scope.pnlSection.toUpperCase()} ${scope.scopeType}`;
    const canExactOverride = canUseExactBottomUpOverride(scope, cell);

    useEffect(() => {
        setMutationError(null);
        setMonthOverrideValue(
            canExactOverride && selectedAssumption?.month_overrides?.[cell.monthKey] != null
                ? String(selectedAssumption.month_overrides[cell.monthKey])
                : "",
        );
        setApplyOverrideForward(false);
        setNotes(selectedAssumption?.notes || "");
        if (!selectedAssumption) {
            setAssumptionMode("direct");
            setTemplateId("");
            setDriverType(scope.kind === "cost" ? "fixed" : "growth");
            setDriverValue("");
            return;
        }
        setAssumptionMode(selectedAssumption.template_id ? "template" : "direct");
        setTemplateId(selectedAssumption.template_id ?? "");
        setDriverType((selectedAssumption.template_id
            ? selectedAssumption.effective_driver_type
            : (selectedAssumption.driver_type || selectedAssumption.effective_driver_type)
        ) as RevenueDriverType);
        setDriverValue(driverValueFromAssumption(selectedAssumption));
    }, [canExactOverride, cell.monthKey, scope.kind, scope.scopeId, scope.scopeType, selectedAssumption]);

    const buildSingleMonthOverride = () => {
        const trimmed = monthOverrideValue.trim();
        if (!trimmed) return {};
        const parsed = Number(trimmed);
        if (!Number.isFinite(parsed)) return {};
        if (!applyOverrideForward) return {[cell.monthKey]: parsed};

        const monthIndex = forecastMonthKeys.indexOf(cell.monthKey);
        const targetMonthKeys = monthIndex >= 0 ? forecastMonthKeys.slice(monthIndex) : [cell.monthKey];
        return Object.fromEntries(targetMonthKeys.map((monthKey) => [monthKey, parsed]));
    };

    const buildMergedMonthOverrides = () => ({
        ...(selectedAssumption?.month_overrides ?? {}),
        ...buildSingleMonthOverride(),
    });

    const refreshAssumptions = async () => {
        if (scope.kind === "revenue") {
            await refreshRevenueAssumptions();
        } else {
            await refreshCostAssumptions();
        }
    };

    const handleSaveDriver = async () => {
        if (assumptionMode === "template" && !templateId) {
            setMutationError("Select a template.");
            return;
        }
        if (assumptionMode === "direct" && !hasValidDriverValue(driverType, driverValue)) {
            setMutationError(`${driverValueLabel(driverType, scope.kind === "cost" ? "cost" : "revenue")} is required.`);
            return;
        }

        setIsSaving(true);
        setMutationError(null);
        try {
            const monthOverrides = canExactOverride ? buildMergedMonthOverrides() : {};
            if (scope.kind === "revenue") {
                if (selectedRevenueAssumption) {
                    await updateCustomerAssumption(workspaceId, selectedRevenueAssumption.id, assumptionMode === "template" ? {
                        template_id: templateId,
                        month_overrides: monthOverrides,
                        notes,
                    } : {
                        driver_type: driverType,
                        params: buildRevenueParams(driverType, driverValue),
                        month_overrides: monthOverrides,
                        notes,
                        template_id: null,
                    });
                } else {
                    await saveCustomerAssumption(workspaceId, {
                        scenario_id: scenarioId,
                        scope_type: scope.scopeType,
                        scope_id: scope.scopeId,
                        scope_label: scope.scopeLabel,
                        template_id: assumptionMode === "template" ? templateId : undefined,
                        driver_type: assumptionMode === "direct" ? driverType : undefined,
                        params: assumptionMode === "direct" ? buildRevenueParams(driverType, driverValue) : undefined,
                        month_overrides: monthOverrides,
                        notes,
                    });
                }
            } else {
                if (selectedCostAssumption) {
                    await updateCostAssumption(workspaceId, selectedCostAssumption.id, assumptionMode === "template" ? {
                        template_id: templateId,
                        month_overrides: monthOverrides,
                        notes,
                    } : {
                        driver_type: driverType,
                        params: buildRevenueParams(driverType, driverValue),
                        month_overrides: monthOverrides,
                        notes,
                        template_id: null,
                    });
                } else {
                    await saveCostAssumption(workspaceId, {
                        scenario_id: scenarioId,
                        pnl_section: scope.pnlSection,
                        scope_type: scope.scopeType,
                        scope_id: scope.scopeId,
                        scope_label: scope.scopeLabel,
                        account_id: scope.accountId,
                        template_id: assumptionMode === "template" ? templateId : undefined,
                        driver_type: assumptionMode === "direct" ? driverType : undefined,
                        params: assumptionMode === "direct" ? buildRevenueParams(driverType, driverValue) : undefined,
                        month_overrides: monthOverrides,
                        notes,
                    });
                }
            }
            await refreshAssumptions();
            await onSaved();
            toast.success("Forecast driver saved");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to save forecast driver";
            setMutationError(message);
            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleBulkApply = async () => {
        if (!scope.canBulkApply) return;
        if (assumptionMode === "template" && !templateId) {
            setMutationError("Select a template.");
            return;
        }
        if (assumptionMode === "direct" && !hasValidDriverValue(driverType, driverValue)) {
            setMutationError(`${driverValueLabel(driverType, scope.kind === "cost" ? "cost" : "revenue")} is required.`);
            return;
        }

        setIsBulkApplying(true);
        setMutationError(null);
        try {
            if (scope.kind === "revenue") {
                if (!scope.bulkScopeType) return;
                await revenueBulkApply(workspaceId, {
                    scenario_id: scenarioId,
                    scope_type: scope.bulkScopeType,
                    scope_id: scope.scopeId,
                    template_id: assumptionMode === "template" ? templateId : undefined,
                    driver_type: assumptionMode === "direct" ? driverType : undefined,
                    params: assumptionMode === "direct" ? buildRevenueParams(driverType, driverValue) : undefined,
                    notes: notes || `Bulk applied from ${scope.scopeLabel}`,
                });
            } else {
                await costBulkApply(workspaceId, {
                    scenario_id: scenarioId,
                    pnl_section: scope.pnlSection,
                    scope_type: scope.scopeType as "parent_group" | "account_group" | "account",
                    scope_id: scope.scopeId,
                    template_id: assumptionMode === "template" ? templateId : undefined,
                    driver_type: assumptionMode === "direct" ? driverType : undefined,
                    params: assumptionMode === "direct" ? buildRevenueParams(driverType, driverValue) : undefined,
                    month_overrides: canExactOverride ? buildSingleMonthOverride() : {},
                    notes: notes || `Bulk applied from ${scope.scopeLabel}`,
                });
            }
            await refreshAssumptions();
            await onSaved();
            toast.success(`Applied driver to child ${scope.kind === "revenue" ? "customers" : "cost rows"}`);
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to bulk apply forecast driver";
            setMutationError(message);
            toast.error(message);
        } finally {
            setIsBulkApplying(false);
        }
    };

    const handleSaveExactOverride = async () => {
        if (!canExactOverride) {
            setMutationError("Exact overrides are only available on leaf rows. Apply a driver to children from this parent row.");
            return;
        }
        const parsed = Number(monthOverrideValue.trim().replace(/[$,\s]/g, ""));
        if (!Number.isFinite(parsed)) {
            setMutationError("Enter a valid override value.");
            return;
        }

        setIsSavingOverride(true);
        setMutationError(null);
        try {
            const updated = await commitVNextModelingWorkbenchBottomUpOverride(workspaceId, {
                scenario_id: scenarioId,
                scope: bottomUpOverrideScopeForAssumptionScope(scope),
                month_key: cell.monthKey,
                value: parsed,
                note: notes,
                n_months: months,
                n_actual_months: actualMonths,
            });
            await refreshAssumptions();
            await onOverrideCommitted(updated);
            toast.success("Exact override saved");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to save exact override";
            setMutationError(message);
            toast.error(message);
        } finally {
            setIsSavingOverride(false);
        }
    };

    const handleClearExactOverride = async () => {
        if (!canExactOverride) return;
        setIsClearingOverride(true);
        setMutationError(null);
        try {
            const updated = await commitVNextModelingWorkbenchBottomUpOverride(workspaceId, {
                scenario_id: scenarioId,
                scope: bottomUpOverrideScopeForAssumptionScope(scope),
                month_key: cell.monthKey,
                value: null,
                clear: true,
                note: notes,
                n_months: months,
                n_actual_months: actualMonths,
            });
            setMonthOverrideValue("");
            await refreshAssumptions();
            await onOverrideCommitted(updated);
            toast.success("Exact override cleared");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to clear exact override";
            setMutationError(message);
            toast.error(message);
        } finally {
            setIsClearingOverride(false);
        }
    };

    return (
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-tour="workbench-forecast-driver-drawer">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <p className="text-sm font-semibold text-slate-950">{canExactOverride ? "Exact override and driver" : "Driver"}</p>
                    <p className="mt-1 text-sm text-slate-600">{scopeDescription}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Badge className="bg-white text-slate-700 hover:bg-white">{cell.monthLabel}</Badge>
                    <Badge className="bg-white text-slate-700 hover:bg-white">Baseline: {baselineMethodLabel(baselineMethod)}</Badge>
                </div>
            </div>

            {canExactOverride ? (
                <div className="mt-4 rounded-lg border border-blue-200 bg-white p-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <p className="text-sm font-medium text-slate-900">Overwrite this forecast cell</p>
                            <p className="mt-1 text-xs text-slate-500">
                                Saves an exact value for {cell.monthLabel}, colours the cell blue, and adds an Ops &amp; Revisions entry.
                            </p>
                        </div>
                        {selectedAssumption?.month_overrides?.[cell.monthKey] != null && (
                            <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50">override active</Badge>
                        )}
                    </div>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                        <Input
                            value={monthOverrideValue}
                            onChange={(event) => setMonthOverrideValue(event.target.value)}
                            aria-label={`${cell.monthLabel} exact override`}
                            inputMode="decimal"
                            placeholder={`Current value ${formatMetricValue(cell.value, "currency")}`}
                            className="bg-white"
                        />
                        <Button
                            type="button"
                            className="bg-[#209DB3] hover:bg-[#1a7d8f]"
                            disabled={isSavingOverride || isClearingOverride}
                            onClick={handleSaveExactOverride}
                        >
                            {isSavingOverride ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                            Save exact override
                        </Button>
                        {selectedAssumption?.month_overrides?.[cell.monthKey] != null && (
                            <Button
                                type="button"
                                variant="outline"
                                disabled={isSavingOverride || isClearingOverride}
                                onClick={handleClearExactOverride}
                            >
                                {isClearingOverride ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                                Clear
                            </Button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-sm font-medium text-slate-900">Parent row driver only</p>
                    <p className="mt-1 text-xs text-slate-500">
                        Exact value overrides are available at leaf level only, so child rows continue to roll up cleanly into this total. Use Apply driver to children for parent-level changes.
                    </p>
                </div>
            )}

            <div className="mt-4 grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                    <Label>Mode</Label>
                    <Select value={assumptionMode} onValueChange={(value) => setAssumptionMode(value as RevenueAssumptionMode)}>
                        <SelectTrigger className="bg-white">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="direct">Direct driver</SelectItem>
                            <SelectItem value="template" disabled={templates.length === 0}>Template</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {assumptionMode === "template" ? (
                    <div className="space-y-2">
                        <Label>Template</Label>
                        <Select value={templateId} onValueChange={setTemplateId}>
                            <SelectTrigger className="bg-white">
                                <SelectValue placeholder={templates.length ? "Select template" : "No templates available"} />
                            </SelectTrigger>
                            <SelectContent>
                                {templates.map((template) => (
                                    <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                ) : (
                    <>
                        <div className="space-y-2">
                            <Label>Driver</Label>
                            <Select value={driverType} onValueChange={(value) => setDriverType(value as RevenueDriverType)}>
                                <SelectTrigger className="bg-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="growth">Growth %</SelectItem>
                                    <SelectItem value="fixed">Fixed monthly value</SelectItem>
                                    <SelectItem value="flat">Flat from trailing actuals</SelectItem>
                                    <SelectItem value="last_year_curve">Last year curve</SelectItem>
                                    <SelectItem value="seasonality">Seasonality</SelectItem>
                                    <SelectItem value="step">Step change</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {driverType !== "flat" && (
                            <div className="space-y-2">
                                <Label>{driverValueLabel(driverType, scope.kind === "cost" ? "cost" : "revenue")}</Label>
                                <Input
                                    value={driverValue}
                                    onChange={(event) => setDriverValue(event.target.value)}
                                    aria-label={driverValueLabel(driverType, scope.kind === "cost" ? "cost" : "revenue")}
                                    inputMode="decimal"
                                    placeholder={driverType === "growth" || driverType === "last_year_curve" ? "e.g. 10" : "e.g. 12000"}
                                />
                            </div>
                        )}
                    </>
                )}

                {canExactOverride && (
                    <>
                        <div className="space-y-2">
                            <Label>{cell.monthLabel} driver override</Label>
                            <Input
                                value={monthOverrideValue}
                                onChange={(event) => setMonthOverrideValue(event.target.value)}
                                aria-label={`${cell.monthLabel} override`}
                                inputMode="decimal"
                                placeholder="Optional exact value saved with driver"
                            />
                        </div>
                        <Label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700 md:col-span-2">
                            <Checkbox
                                checked={applyOverrideForward}
                                onCheckedChange={(checked) => setApplyOverrideForward(checked === true)}
                                disabled={!monthOverrideValue.trim()}
                                aria-label="Apply exact override to this and future forecast months"
                            />
                            <span>
                                Apply exact override to this and future forecast months
                            </span>
                        </Label>
                    </>
                )}
                <div className="space-y-2">
                    <Label>Notes</Label>
                    <Input value={notes} onChange={(event) => setNotes(event.target.value)} aria-label="Forecast driver notes" placeholder="Optional note" />
                </div>
            </div>

            {mutationError && <p className="mt-3 text-sm text-rose-600">{mutationError}</p>}
            <div className="mt-4 flex flex-wrap gap-2">
                {canExactOverride && (
                    <Button type="button" onClick={handleSaveDriver} disabled={isSaving || isBulkApplying}>
                        {isSaving ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                        Save driver on this row
                    </Button>
                )}
                {scope.canBulkApply && (
                    <Button type="button" variant="outline" onClick={handleBulkApply} disabled={isSaving || isBulkApplying}>
                        {isBulkApplying ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                        Apply driver to children
                    </Button>
                )}
            </div>
        </div>
    );
}

function WorkbenchCellSheet({
    workspaceId,
    scenarioId,
    months,
    actualMonths,
    baselineMethod,
    forecastMonthKeys,
    cell,
    opsHistory,
    onClose,
    onCommitted,
    onForecastDriverSaved,
}: {
    workspaceId: string;
    scenarioId: string;
    months: number;
    actualMonths: number;
    baselineMethod: string;
    forecastMonthKeys: string[];
    cell: SelectedWorkbenchCell | null;
    opsHistory: ModelingWorkbenchOpHistoryItem[];
    onClose: () => void;
    onCommitted: (data: ModelingWorkbenchData) => Promise<void>;
    onForecastDriverSaved: () => Promise<void>;
}) {
    const forecastAssumptionScope = useMemo(() => forecastAssumptionScopeForCell(cell), [cell]);
    const canExactBottomUpOverride = Boolean(
        cell && forecastAssumptionScope && canUseExactBottomUpOverride(forecastAssumptionScope, cell),
    );
    const {data, isLoading, error} = useVNextModelingWorkbenchCellDetail(
        workspaceId,
        forecastAssumptionScope ? null : cell?.statement ?? null,
        forecastAssumptionScope ? null : cell?.nodeId ?? null,
        forecastAssumptionScope ? null : cell?.monthKey ?? null,
        scenarioId,
        months,
        actualMonths,
    );
    const [draftValue, setDraftValue] = useState("");
    const [saveError, setSaveError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [revertingOpId, setRevertingOpId] = useState<string | null>(null);

    useEffect(() => {
        if (!cell) {
            setDraftValue("");
            setSaveError(null);
            return;
        }
        setDraftValue(String(cell.value));
        setSaveError(null);
    }, [cell]);

    const relevantHistory = useMemo(() => {
        if (!cell) return [];
        return opsHistory.filter((op) => op.target_node_id === cell.nodeId && op.target_month === cell.monthKey);
    }, [opsHistory, cell]);

    const canOverrideCell = Boolean(
        cell
        && data?.is_editable
        && cell.status === "forecast"
        && !cell.inlineLevel
        && (
            cell.rowType === "account"
            || Boolean(cell.sourceAccountId)
            || !cell.nodeId.includes(":")
        ),
    );

    const handleSave = async (event?: FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        if (!cell || !canOverrideCell) return;
        const parsed = parseOverrideValue(draftValue);
        if (!Number.isFinite(parsed)) {
            setSaveError("Enter a valid numeric override.");
            return;
        }
        setIsSaving(true);
        setSaveError(null);
        try {
            const ops: VNextOpInput[] = [{
                op_type: "set_override",
                target_node_id: cell.nodeId,
                target_month: cell.monthKey,
                label: `Workbench override: ${cell.nodeLabel}`,
                params: {value: parsed},
            }];
            const updated = await commitVNextModelingWorkbenchEdit(workspaceId, {
                scenario_id: scenarioId,
                ops,
                n_months: months,
                n_actual_months: actualMonths,
                created_by: "human",
            });
            await onCommitted(updated);
            onClose();
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to save override");
        } finally {
            setIsSaving(false);
        }
    };

    const handleRevert = async (opId: string) => {
        setRevertingOpId(opId);
        setSaveError(null);
        try {
            const updated = await revertVNextModelingWorkbenchOps(workspaceId, {
                scenario_id: scenarioId,
                op_ids: [opId],
                n_months: months,
                n_actual_months: actualMonths,
            });
            await onCommitted(updated);
            onClose();
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to revert revision");
        } finally {
            setRevertingOpId(null);
        }
    };

    return (
        <Sheet open={Boolean(cell)} onOpenChange={(open) => !open && onClose()}>
            <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl" data-tour="workbench-cell-detail-drawer">
                {cell && (
                    <>
                        <SheetHeader>
                            <SheetTitle>{cell.nodeLabel}</SheetTitle>
                            <SheetDescription>
                                {cell.monthLabel} · {cell.statement.toUpperCase()} · {cell.status}
                            </SheetDescription>
                        </SheetHeader>
                        <div className="mt-6 space-y-4">
                            {forecastAssumptionScope && (
                                <>
                                    <div className="grid gap-3 md:grid-cols-2">
                                        <Card className="border-slate-200 shadow-none">
                                            <CardContent className="p-4">
                                                <p className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-500">Grid Value</p>
                                                <p className="mt-2 text-xl font-semibold text-slate-950">{formatMetricValue(cell.value, "currency")}</p>
                                            </CardContent>
                                        </Card>
                                        <Card className="border-slate-200 shadow-none">
                                            <CardContent className="p-4">
                                                <p className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-500">Edit Scope</p>
                                                <p className="mt-2 text-sm font-semibold text-slate-950">{cell.nodeLabel}</p>
                                                <p className="mt-1 text-xs text-slate-500">{forecastAssumptionScope.kind === "revenue" ? forecastAssumptionScope.scopeType : forecastAssumptionScope.scopeType.replace("_", " ")}</p>
                                            </CardContent>
                                        </Card>
                                    </div>
                                    <div className="rounded-xl border border-slate-200 p-4">
                                        <div className="flex flex-wrap gap-2">
                                            <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                                                method: bottom-up {forecastAssumptionScope.kind}
                                            </Badge>
                                            <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                                                scope: {forecastAssumptionScope.kind === "revenue" ? forecastAssumptionScope.scopeType : forecastAssumptionScope.scopeType.replace("_", " ")}
                                            </Badge>
                                            {cell.value !== 0 && <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50">editable forecast</Badge>}
                                        </div>
                                        <p className="mt-4 text-sm text-slate-600">
                                            Formula: selected baseline is {baselineMethodFormula(baselineMethod)}. That baseline feeds the selected driver or template for this {forecastAssumptionScope.kind === "revenue" ? forecastAssumptionScope.scopeType : forecastAssumptionScope.scopeType.replace("_", " ")}; active Ops &amp; Revisions are applied on top. {canExactBottomUpOverride ? `An exact ${cell.monthLabel} leaf override takes precedence when active. ` : "Exact overrides are disabled here because this row rolls up from child rows; use Apply driver to children so the children and total tie. "}Leaf-level bottom-up values roll up into the P&amp;L, cash flow, balance sheet, KPIs, and revision history.
                                        </p>
                                    </div>
                                    <ForecastDriverEditor
                                        workspaceId={workspaceId}
                                        scenarioId={scenarioId}
                                        months={months}
                                        actualMonths={actualMonths}
                                        baselineMethod={baselineMethod}
                                        cell={cell}
                                        scope={forecastAssumptionScope}
                                        forecastMonthKeys={forecastMonthKeys}
                                        onSaved={onForecastDriverSaved}
                                        onOverrideCommitted={async (updated) => {
                                            await onCommitted(updated);
                                            await onForecastDriverSaved();
                                            onClose();
                                        }}
                                    />
                                </>
                            )}
                            {!forecastAssumptionScope && isLoading && <Skeleton className="h-48 w-full rounded-xl" />}
                            {!forecastAssumptionScope && error && <p className="text-sm text-rose-600">{error}</p>}
                            {!forecastAssumptionScope && !isLoading && !error && data && (
                                <Tabs defaultValue="detail" className="space-y-4">
                                    <TabsList>
                                        <TabsTrigger value="detail">Detail</TabsTrigger>
                                        <TabsTrigger value="ops">Ops</TabsTrigger>
                                        <TabsTrigger value="history">History</TabsTrigger>
                                    </TabsList>

                                    <TabsContent value="detail" className="space-y-4">
                                        <div className="grid gap-3 md:grid-cols-2">
                                            <Card className="border-slate-200 shadow-none">
                                                <CardContent className="p-4">
                                                    <p className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-500">Current Value</p>
                                                    <p className="mt-2 text-xl font-semibold text-slate-950">{formatMetricValue(data.value, "currency")}</p>
                                                </CardContent>
                                            </Card>
                                            <Card className="border-slate-200 shadow-none">
                                                <CardContent className="p-4">
                                                    <p className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-500">Baseline</p>
                                                    <p className="mt-2 text-xl font-semibold text-slate-950">{formatMetricValue(data.baseline, "currency")}</p>
                                                </CardContent>
                                            </Card>
                                        </div>

                                        <div className="rounded-xl border border-slate-200 p-4">
                                            <div className="flex flex-wrap gap-2">
                                                <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">method: {data.method || "n/a"}</Badge>
                                                <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">role: {data.role || "n/a"}</Badge>
                                                {data.is_override && <Badge className="bg-sky-50 text-sky-700 hover:bg-sky-50">override</Badge>}
                                                {data.linked_from && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">linked from {data.linked_from}</Badge>}
                                                {!data.is_editable && <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">derived / locked</Badge>}
                                            </div>
                                            <p className="mt-4 text-sm text-slate-600">{data.calculation || "No calculation trace provided for this cell yet."}</p>
                                        </div>

                                        {canOverrideCell && (
                                            <div className="rounded-xl border border-slate-200 p-4">
                                                <p className="text-sm font-medium text-slate-900">Override forecast cell</p>
                                                <p className="mt-1 text-sm text-slate-500">Commit a structured override and refresh KPIs and all three statements.</p>
                                                <form className="mt-3 flex flex-col gap-3 sm:flex-row" onSubmit={handleSave}>
                                                    <Input value={draftValue} onChange={(event) => setDraftValue(event.target.value)} aria-label="Override value" inputMode="decimal" />
                                                    <Button type="submit" disabled={isSaving}>
                                                        {isSaving ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                                                        Save override
                                                    </Button>
                                                </form>
                                                {saveError && <p className="mt-2 text-sm text-rose-600">{saveError}</p>}
                                            </div>
                                        )}
                                    </TabsContent>

                                    <TabsContent value="ops" className="space-y-3">
                                        {data.ops_applied.length === 0 ? (
                                            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-sm text-slate-500">No direct ops on this cell.</div>
                                        ) : (
                                            data.ops_applied.map((op, index) => (
                                                <div key={`${cell.nodeId}-op-${index}`} className="rounded-xl border border-slate-200 p-4">
                                                    <p className="text-sm font-medium text-slate-900">{String(op.label ?? op.op_type ?? `Operation ${index + 1}`)}</p>
                                                    <pre className="mt-2 overflow-x-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-600">{JSON.stringify(op, null, 2)}</pre>
                                                </div>
                                            ))
                                        )}
                                    </TabsContent>

                                    <TabsContent value="history" className="space-y-3">
                                        {relevantHistory.length === 0 ? (
                                            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-sm text-slate-500">No revision history for this exact cell yet.</div>
                                        ) : (
                                            relevantHistory.map((op) => (
                                                <div key={op.id} className="rounded-xl border border-slate-200 p-4">
                                                    <div className="flex items-center justify-between gap-3">
                                                        <p className="text-sm font-medium text-slate-900">{op.label}</p>
                                                        <div className="flex items-center gap-2">
                                                            {op.is_active && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">active</Badge>}
                                                            {op.is_active && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    disabled={revertingOpId === op.id}
                                                                    onClick={() => handleRevert(op.id)}
                                                                >
                                                                    {revertingOpId === op.id ? (
                                                                        <>
                                                                            <Loader2Icon className="mr-2 size-4 animate-spin" />
                                                                            Reverting
                                                                        </>
                                                                    ) : "Revert"}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <p className="mt-1 text-xs uppercase tracking-wide text-slate-500">{op.op_type}</p>
                                                    <pre className="mt-2 overflow-x-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-600">{JSON.stringify(op.params, null, 2)}</pre>
                                                </div>
                                            ))
                                        )}
                                    </TabsContent>
                                </Tabs>
                            )}
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}

function ModelingStatementSection({
    workspaceId,
    scenarioId,
    months,
    actualMonths,
    baselineMethod,
    statementKey,
    statement,
    rows,
    isOpen,
    onOpenChange,
    expanded,
    expandedRows,
    expandedLoading,
    expandedReconciliation,
    visibility,
    inlineRevenueGrid,
    inlineCostGrid,
    inlineRevenueOverrides,
    inlineCostOverrides,
    opsHistory,
    onToggleExpand,
    onCellSelect,
    onCommitted,
    onForecastDriverSaved,
    onRebuild,
}: {
    workspaceId: string;
    scenarioId: string;
    months: number;
    actualMonths: number;
    baselineMethod: string;
    statementKey: StatementKey;
    statement: ModelingWorkbenchStatement;
    rows: ModelingWorkbenchRow[];
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    expanded: Record<string, boolean>;
    expandedRows: Record<string, ModelingWorkbenchRow[]>;
    expandedLoading: Record<string, boolean>;
    expandedReconciliation: Record<string, Record<string, ExpandReconciliationEntry>>;
    visibility: RowVisibilityOptions;
    inlineRevenueGrid?: RevenueBottomUpData | null;
    inlineCostGrid?: CostBottomUpData | null;
    inlineRevenueOverrides?: BottomUpOverrideMonthMap;
    inlineCostOverrides?: BottomUpOverrideMonthMap;
    opsHistory: ModelingWorkbenchOpHistoryItem[];
    onToggleExpand: (statementKey: StatementKey, row: ModelingWorkbenchRow) => void;
    onCellSelect: (cell: SelectedWorkbenchCell) => void;
    onCommitted: (data: ModelingWorkbenchData) => Promise<void>;
    onForecastDriverSaved: () => Promise<void>;
    onRebuild?: () => Promise<void>;
}) {
    const [isRebuilding, setIsRebuilding] = useState(false);
    const horizontalScrollTargetRef = useRef<HTMLElement | null>(null);
    const monthHeaderRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
    const actualCount = statement.months.filter((month) => month.status === "actual").length;
    const forecastCount = statement.months.length - actualCount;
    const forecastStartIndex = getForecastStartIndex(statement.months);
    const displayRows = flattenStatementRows(rows, expanded, expandedRows, visibility);
    const isWideHorizon = statement.months.length > 12;
    const firstMonth = statement.months[0];
    const forecastStartMonth = statement.months[forecastStartIndex] ?? null;
    const lastMonth = statement.months.at(-1) ?? null;

    const scrollToMonth = (monthKey: string | undefined | null) => {
        if (!monthKey) return;
        const target = horizontalScrollTargetRef.current;
        const header = monthHeaderRefs.current[monthKey];
        if (!target || !header) return;
        const stickyLineItemWidth = isWideHorizon ? 240 : 280;
        target.scrollTo({
            left: Math.max(header.offsetLeft - stickyLineItemWidth - 16, 0),
            behavior: "smooth",
        });
    };

    return (
        <Collapsible open={isOpen} onOpenChange={onOpenChange}>
            <section className="rounded-2xl border border-[#E2E8F0] bg-white shadow-sm">
                <CollapsibleTrigger asChild>
                    <button className="flex w-full items-center justify-between gap-4 rounded-2xl bg-gradient-to-r from-slate-950 via-slate-900 to-slate-800 px-5 py-4 text-left text-white">
                        <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[2px] text-sky-200">Statement</p>
                            <h3 className="mt-1 text-lg font-semibold">{statement.label}</h3>
                            <p className="mt-1 text-sm text-slate-300">{actualCount} actual + {forecastCount} forecast months</p>
                        </div>
                        <ChevronDownIcon className={cn("size-5 transition-transform", isOpen && "rotate-180")} />
                    </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="px-5 pb-5 pt-4">
                    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <p className="text-sm text-slate-500">Expand section rows to account level, click any cell for detail, and use forecast overrides to drive KPI flow-through.</p>
                            {onRebuild && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={isRebuilding}
                                    onClick={async () => {
                                        setIsRebuilding(true);
                                        try { await onRebuild(); } finally { setIsRebuilding(false); }
                                    }}
                                >
                                    {isRebuilding ? <Loader2Icon className="mr-1.5 size-3.5 animate-spin" /> : <RefreshCwIcon className="mr-1.5 size-3.5" />}
                                    {isRebuilding ? "Rebuilding…" : "Rebuild from sync"}
                                </Button>
                            )}
                        </div>
                        {isWideHorizon ? (
                            <div className="flex flex-wrap items-center gap-2">
                                <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">24-column mode</Badge>
                                <Button size="sm" variant="outline" onClick={() => scrollToMonth(firstMonth?.month_key)}>
                                    Actuals start
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => scrollToMonth(forecastStartMonth?.month_key)}>
                                    Forecast start
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => scrollToMonth(lastMonth?.month_key)}>
                                    Latest month
                                </Button>
                            </div>
                        ) : null}
                    </div>
                    <TooltipProvider delayDuration={120}>
                    <StickyHorizontalScrollFrame
                        viewportClassName="max-h-[36rem] overflow-y-auto pr-1"
                        scrollTargetRef={horizontalScrollTargetRef}
                    >
                        <div className="rounded-xl border border-slate-200">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-slate-50">
                                        <TableHead className={cn("sticky left-0 top-0 z-30 bg-slate-50", isWideHorizon ? "min-w-[240px]" : "min-w-[280px]")}>Line Item</TableHead>
                                        {statement.months.map((month, monthIndex) => (
                                            <TableHead
                                                key={`${statement.key}-${month.month_key}`}
                                                ref={(element) => {
                                                    monthHeaderRefs.current[month.month_key] = element;
                                                }}
                                                title={
                                                    month.source === "xero_snapshot" ? "Actual: sourced from Xero report snapshot" :
                                                    month.source === "xero_journal" ? "Actual: sourced from Xero journals" :
                                                    month.source === "xero_derived" ? "Actual: derived from Xero data" :
                                                    month.source === "model_assumption" ? "Forecast: generated from model assumptions" :
                                                    undefined
                                                }
                                                className={cn(
                                                    "sticky top-0 z-20 text-right",
                                                    isWideHorizon ? "min-w-[108px]" : "min-w-[124px]",
                                                    month.status === "forecast" ? "bg-amber-50/70" : "bg-slate-50",
                                                    monthIndex === forecastStartIndex ? "border-l-2 border-l-amber-300" : "",
                                                )}
                                            >
                                                <div className="flex items-center justify-end gap-2">
                                                    <span>{month.label}</span>
                                                    <Badge
                                                        variant="outline"
                                                        className={cn(
                                                            month.status === "forecast"
                                                                ? "border-amber-300 bg-amber-100 text-amber-700"
                                                                : "border-teal-300 bg-teal-50 text-teal-700",
                                                            isWideHorizon ? "px-1 text-[9px]" : "text-[10px]",
                                                        )}
                                                    >
                                                        {month.status === "forecast" ? "forecast" : "actual"}
                                                    </Badge>
                                                </div>
                                            </TableHead>
                                        ))}
                                        <TableHead className={cn("sticky right-0 top-0 z-30 bg-slate-50 text-right", isWideHorizon ? "min-w-[120px]" : "min-w-[140px]")}>Total</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {displayRows.map((row, rowIndex) => {
                                        const isVarianceRow = row.row_type === "variance";
                                        const hasRevenueInlineChildren =
                                            statementKey === "pnl"
                                            && Boolean(buildRevenueInlineChildren(row, inlineRevenueGrid, statement, inlineRevenueOverrides)?.length);
                                        const hasCostInlineChildren =
                                            statementKey === "pnl"
                                            && Boolean(buildCostInlineChildren(row, inlineCostGrid, statement, inlineCostOverrides)?.length);
                                        const canToggle =
                                            !isVarianceRow
                                            && (
                                                hasRevenueInlineChildren
                                                || hasCostInlineChildren
                                                || (
                                                    row.is_expandable
                                                    && (
                                                        statementKey !== "pnl"
                                                        || row.row_type !== "account"
                                                        || !isRevenueRow(row)
                                                    )
                                                )
                                            );
                                        // Look up reconciliation data for variance row tooltip
                                        let varianceRecon: Record<string, ExpandReconciliationEntry> | null = null;
                                        if (isVarianceRow) {
                                            for (let i = rowIndex; i >= 0; i--) {
                                                if (displayRows[i].depth === 0) {
                                                    varianceRecon = expandedReconciliation[displayRows[i].node_id] ?? null;
                                                    break;
                                                }
                                            }
                                        }

                                        return (
                                            <React.Fragment key={`${statement.key}-${row.node_id}`}>
                                                <TableRow className={cn("hover:bg-slate-50/80", isVarianceRow && "bg-amber-50/40")}>
                                                    <TableCell className="sticky left-0 z-10 bg-white align-top">
                                                        <div className="flex items-start gap-2" style={{paddingLeft: `${row.depth * 18}px`}}>
                                                            {canToggle ? (
                                                                <button
                                                                    type="button"
                                                                    aria-label={`Toggle ${row.label}`}
                                                                    className="mt-0.5 rounded p-1 hover:bg-slate-100"
                                                                    onClick={() => onToggleExpand(statementKey, row)}
                                                                >
                                                                    {expanded[row.node_id] ? (
                                                                        <ChevronDownIcon className="size-4 text-slate-600" />
                                                                    ) : (
                                                                        <ChevronRightIcon className="size-4 text-slate-600" />
                                                                    )}
                                                                </button>
                                                            ) : (
                                                                <div className="mt-0.5 size-6 shrink-0" />
                                                            )}
                                                            <div className={cn("min-w-0", isWideHorizon && "max-w-[200px]")}>
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <p className={cn(
                                                                        "text-sm",
                                                                        isVarianceRow
                                                                            ? "italic text-amber-600"
                                                                            : cn("text-slate-900", row.is_bold && "font-semibold", !row.is_bold && "font-medium"),
                                                                    )}>{row.label}</p>
                                                                </div>
                                                                {isVarianceRow && varianceRecon ? (
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <p className="cursor-help text-[10px] italic text-amber-500 underline decoration-dotted">Reconciliation detail</p>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent side="right" className="max-w-[340px] text-xs">
                                                                            <div className="space-y-1">
                                                                                {Object.entries(varianceRecon).map(([mk, r]) => (
                                                                                    <div key={mk} className="flex items-center gap-2">
                                                                                        <span className="font-medium text-slate-700">{mk}:</span>
                                                                                        <span>Snapshot {formatCellValue(r.snapshot_total, "")}</span>
                                                                                        <span>Accounts {formatCellValue(r.account_total, "")}</span>
                                                                                        <span className={r.status === "reconciled" ? "text-emerald-600" : "text-amber-600"}>
                                                                                            Var {formatCellValue(r.variance, "")}
                                                                                        </span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                ) : !isVarianceRow ? (
                                                                    <p className="text-[11px] text-slate-500">{row.category_key || row.row_type}</p>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    {statement.months.map((month, monthIndex) => {
                                                        const cell = row.values[month.month_key];
                                                        const isForecast = cell?.status === "forecast";
                                                        const selectedCellForMonth: SelectedWorkbenchCell | null = cell ? {
                                                            statement: statementKey,
                                                            nodeId: row.node_id,
                                                            nodeLabel: row.label,
                                                            monthKey: month.month_key,
                                                            monthLabel: month.label,
                                                            status: cell.status,
                                                            value: cell.value,
                                                            rowType: row.row_type,
                                                            categoryKey: row.category_key,
                                                            inlineLevel: row.inline_level,
                                                            isExpandable: row.is_expandable,
                                                            segment: row.segment,
                                                            parentGroup: row.parent_group,
                                                            accountGroup: row.account_group,
                                                            parentContactId: row.parent_contact_id,
                                                            contactId: row.contact_id,
                                                            sourceAccountId: row.source_account_id,
                                                            xeroAccountId: row.xero_account_id,
                                                        } : null;
                                                        const candidateQuickOverrideScope = selectedCellForMonth
                                                            && cell?.status === "forecast"
                                                            ? forecastAssumptionScopeForCell(selectedCellForMonth)
                                                            : null;
                                                        const quickOverrideScope = selectedCellForMonth && candidateQuickOverrideScope && canUseExactBottomUpOverride(candidateQuickOverrideScope, selectedCellForMonth)
                                                            ? candidateQuickOverrideScope
                                                            : null;
                                                        return (
                                                            <TableCell
                                                                key={`${row.node_id}-${month.month_key}`}
                                                                className={cn(
                                                                    "text-right tabular-nums text-xs transition-colors",
                                                                    !isVarianceRow && !quickOverrideScope && "cursor-pointer hover:bg-sky-50",
                                                                    cell?.is_override ? "bg-blue-50 hover:bg-blue-100" : isForecast ? "bg-amber-50/30" : "",
                                                                    monthIndex === forecastStartIndex ? "border-l-2 border-l-amber-200" : "",
                                                                    isVarianceRow && "italic text-amber-600/80",
                                                                )}
                                                                onClick={() => {
                                                                    if (!isVarianceRow && selectedCellForMonth && !quickOverrideScope) {
                                                                        onCellSelect(selectedCellForMonth);
                                                                    }
                                                                }}
                                                                title={
                                                                    isVarianceRow
                                                                        ? `Variance for ${month.label}`
                                                                        : quickOverrideScope
                                                                            ? undefined
                                                                            : `Open detail for ${row.label} in ${month.label}`
                                                                }
                                                            >
                                                                {cell ? (
                                                                    isVarianceRow ? (
                                                                        <span className="italic text-amber-600/80">{formatCellValue(cell.value, row.category_key)}</span>
                                                                    ) : quickOverrideScope && selectedCellForMonth ? (
                                                                        <WorkbenchForecastOverridePopover
                                                                            workspaceId={workspaceId}
                                                                            scenarioId={scenarioId}
                                                                            months={months}
                                                                            actualMonths={actualMonths}
                                                                            baselineMethod={baselineMethod}
                                                                            cell={selectedCellForMonth}
                                                                            cellValue={cell}
                                                                            scope={quickOverrideScope}
                                                                            opsHistory={opsHistory}
                                                                            rowIsBold={row.is_bold}
                                                                            categoryKey={row.category_key}
                                                                            onCommitted={onCommitted}
                                                                            onForecastDriverSaved={onForecastDriverSaved}
                                                                            onOpenDetail={onCellSelect}
                                                                        />
                                                                    ) : (
                                                                        <Tooltip>
                                                                            <TooltipTrigger asChild>
                                                                                <div className="flex items-center justify-end gap-1">
                                                                                    {cell.has_ops && !cell.is_override && <span className="size-2 rounded-full bg-sky-500" />}
                                                                                    {cell.is_override && <span className="size-2 rounded-full bg-blue-500" />}
                                                                                    <span className={cn("text-slate-900", row.is_bold && "font-semibold", cell.is_override && "text-blue-700")}>{formatCellValue(cell.value, row.category_key)}</span>
                                                                                </div>
                                                                            </TooltipTrigger>
                                                                            <TooltipContent side="top" className="max-w-[260px] text-xs">
                                                                                <div className="space-y-1">
                                                                                    <p className="font-medium text-slate-900">{row.label}</p>
                                                                                    <p className="text-slate-600">{getCellHoverSummary(row, cell, month.label)}</p>
                                                                                    {cell.has_ops && !cell.is_override && <p className="text-sky-700">Scenario ops applied</p>}
                                                                                    {cell.is_override && <p className="text-blue-700">Manual override active</p>}
                                                                                </div>
                                                                            </TooltipContent>
                                                                        </Tooltip>
                                                                    )
                                                                ) : (
                                                                    <span className="text-slate-400">—</span>
                                                                )}
                                                            </TableCell>
                                                        );
                                                    })}
                                                    <TableCell className={cn("sticky right-0 z-10 bg-white text-right tabular-nums", isVarianceRow && "italic text-amber-600/80")}>
                                                        <span className={cn(
                                                            "text-sm",
                                                            isVarianceRow
                                                                ? "italic text-amber-600"
                                                                : cn("text-slate-900", row.is_bold && "font-semibold"),
                                                        )}>{/opening_cash|closing_cash/.test(row.node_id) ? "" : formatCellValue(row.total, row.category_key)}</span>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })}
                                    {displayRows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={statement.months.length + 2} className="py-8 text-center text-sm text-slate-500">
                                                No rows available.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </StickyHorizontalScrollFrame>
                    </TooltipProvider>
                    {Object.values(expandedLoading).some(Boolean) && (
                        <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                            <Loader2Icon className="size-3.5 animate-spin" />
                            Loading expanded rows…
                        </div>
                    )}
                </CollapsibleContent>
            </section>
        </Collapsible>
    );
}

export function ModelingWorkbenchTab({
    workspaceId,
    scenarioId,
    actualMonths = 12,
    forecastMonths = 6,
}: ModelingWorkbenchTabProps) {
    const {mutate} = useSWRConfig();
    const [workbenchBaselineMethod, setWorkbenchBaselineMethod] = useState("avg_6m");
    const [workbenchForecastMonths, setWorkbenchForecastMonths] = useState(forecastMonths);
    const [isUpdatingBaseline, setIsUpdatingBaseline] = useState(false);
    const {data, isLoading, error, refresh} = useVNextModelingWorkbench(
        workspaceId,
        scenarioId,
        workbenchForecastMonths,
        actualMonths,
    );
    const {grid: inlineRevenueGrid} = useRevenueBottomUp(
        workspaceId,
        scenarioId,
        actualMonths,
        workbenchForecastMonths,
    );
    const {grid: inlineCostGrid} = useCostBottomUp(
        workspaceId,
        scenarioId,
        actualMonths,
        workbenchForecastMonths,
    );
    const displayKpis = data?.kpis ?? [];
    const [selectedCell, setSelectedCell] = useState<SelectedWorkbenchCell | null>(null);
    const [assumptionsOpen, setAssumptionsOpen] = useState(false);
    const [openSections, setOpenSections] = useState<Record<StatementKey, boolean>>({pnl: true, bs: true, cf: true});
    const [expanded, setExpanded] = useState<ExpandedState>(EMPTY_EXPANDED);
    const [expandedRows, setExpandedRows] = useState<ExpandedRowsState>(EMPTY_CHILDREN);
    const [expandedLoading, setExpandedLoading] = useState<ExpandedLoadingState>(EMPTY_LOADING);
    const [expandedReconciliation, setExpandedReconciliation] = useState<ExpandedReconciliationState>(EMPTY_RECONCILIATION);
    const [expandError, setExpandError] = useState<string | null>(null);
    // Expanded child rows are cached at expand time; drop them when the scenario
    // changes or they keep showing the previous scenario's values and override dots.
    useEffect(() => {
        setExpanded(EMPTY_EXPANDED);
        setExpandedRows(EMPTY_CHILDREN);
        setExpandedLoading(EMPTY_LOADING);
        setExpandedReconciliation(EMPTY_RECONCILIATION);
    }, [workspaceId, scenarioId]);
    const [visibility, setVisibility] = useState<RowVisibilityOptions>({hideZeroRows: false, showEditedRowsOnly: false});
    const [workbenchTourOpen, setWorkbenchTourOpen] = useState(false);
    const [tourActiveTab, setTourActiveTab] = useState("modeling_workbench");
    const resolvedActualMonths = data?.meta.n_actual_months ?? actualMonths;
    const nodeLabels = useMemo(
        () => buildWorkbenchNodeLabelMap(data?.statements, expandedRows),
        [data?.statements, expandedRows],
    );
    const selectedStatementForecastMonthKeys = useMemo(() => {
        if (!data || !selectedCell) return [];
        return data.statements[selectedCell.statement].months
            .filter((month) => month.status === "forecast")
            .map((month) => month.month_key);
    }, [data, selectedCell]);
    const revenueOverrideMonths = useMemo<BottomUpOverrideMonthMap>(
        () => bottomUpOverrideMonthsFromOps(data?.ops_history, "revenue"),
        [data?.ops_history],
    );
    const costOverrideMonths = useMemo<BottomUpOverrideMonthMap>(
        () => bottomUpOverrideMonthsFromOps(data?.ops_history, "cost"),
        [data?.ops_history],
    );
    const handleRebuild = async (statementKey: StatementKey) => {
        const endpointMap: Record<StatementKey, string> = {
            pnl: "rebuild-pnl",
            bs: "rebuild-bs",
            cf: "rebuild-cf",
        };
        const labelMap: Record<StatementKey, string> = {pnl: "P&L", bs: "BS", cf: "CF"};
        const endpoint = endpointMap[statementKey];
        const label = labelMap[statementKey];

        try {
            const Cookies = (await import("js-cookie")).default;
            const csrfToken = Cookies.get("csrftoken") || "";
            const resp = await fetch(`/api/model-vnext/workspace/${workspaceId}/${endpoint}/`, {
                method: "POST",
                headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
                credentials: "include",
            });
            const body = await resp.json();
            if (!resp.ok) {
                toast.error(body.errors?.[0] || `${label} rebuild failed (${resp.status})`);
            } else {
                const r = body.data || body;
                toast.success(`${label} rebuilt: ${r.nodes_created} accounts, ${r.series_created} values`);
                refresh();
                mutate((key: unknown) => typeof key === "string" && key.startsWith(`/api/model-vnext/workspace/${workspaceId}/`));
            }
        } catch (e) {
            toast.error(e instanceof Error ? e.message : `${label} rebuild failed`);
        }
    };

    const handleCellSelect = (cell: SelectedWorkbenchCell) => {
        setSelectedCell(cell);
    };

    useEffect(() => {
        if (data?.meta.scenario.baseline_method) {
            setWorkbenchBaselineMethod(data.meta.scenario.baseline_method);
        }
    }, [data?.meta.scenario.baseline_method]);

    useEffect(() => {
        setWorkbenchForecastMonths((previous: number) => (previous === forecastMonths ? previous : forecastMonths));
    }, [forecastMonths]);

    useEffect(() => {
        const handler = (event: Event) => {
            const nextTab = (event as CustomEvent<{tab?: string}>).detail?.tab;
            if (nextTab) setTourActiveTab(nextTab);
        };
        window.addEventListener("dashboard-tab-changed", handler);
        return () => window.removeEventListener("dashboard-tab-changed", handler);
    }, []);

    const handleTourTabChange = (tab: string) => {
        setTourActiveTab(tab);
        window.dispatchEvent(new CustomEvent("vnext-ui-action", {detail: {tab}}));
    };

    const handleBaselineMethodChange = async (method: string) => {
        const previous = workbenchBaselineMethod;
        setWorkbenchBaselineMethod(method);
        setIsUpdatingBaseline(true);
        try {
            await patchScenario(workspaceId, {
                scenario_id: scenarioId,
                baseline_method: method,
            });
            await mutate((key: unknown) => typeof key === "string" && key.startsWith(`/api/model-vnext/workspace/${workspaceId}/`));
            toast.success("Baseline method updated");
        } catch (err) {
            setWorkbenchBaselineMethod(previous);
            toast.error(err instanceof Error ? err.message : "Failed to update baseline method");
        } finally {
            setIsUpdatingBaseline(false);
        }
    };

    const handleToggleExpand = async (statementKey: StatementKey, row: ModelingWorkbenchRow) => {
        const statement = data?.statements[statementKey];
        if (!statement) return;

        const inlineRows =
            statementKey === "pnl"
                ? buildRevenueInlineChildren(row, inlineRevenueGrid, statement, revenueOverrideMonths)
                    ?? buildCostInlineChildren(row, inlineCostGrid, statement, costOverrideMonths)
                : null;
        const pnlExpandRequest = statementKey === "pnl" ? getPnlWorkbenchExpandRequest(row) : null;
        if (statementKey === "pnl" && row.row_type === "account" && isRevenueRow(row) && !inlineRows?.length) return;
        if (!row.is_expandable && !inlineRows?.length && !pnlExpandRequest) return;

        setExpandError(null);
        if (expandedRows[statementKey][row.node_id]) {
            setExpanded((prev) => ({
                ...prev,
                [statementKey]: {
                    ...prev[statementKey],
                    [row.node_id]: !prev[statementKey][row.node_id],
                },
            }));
            return;
        }

        setExpandedLoading((prev) => ({
            ...prev,
            [statementKey]: {...prev[statementKey], [row.node_id]: true},
        }));

        try {
            const {rows, reconciliation} = inlineRows?.length
                ? {rows: inlineRows, reconciliation: undefined}
                : await fetchVNextModelingWorkbenchExpand(
                    workspaceId,
                    statementKey,
                    pnlExpandRequest?.sectionKey ?? row.node_id,
                    scenarioId,
                    workbenchForecastMonths,
                    actualMonths,
                    pnlExpandRequest?.options,
                );
            setExpandedRows((prev) => ({
                ...prev,
                [statementKey]: {...prev[statementKey], [row.node_id]: rows},
            }));
            if (reconciliation) {
                setExpandedReconciliation((prev) => ({
                    ...prev,
                    [statementKey]: {...prev[statementKey], [row.node_id]: reconciliation},
                }));
            }
            setExpanded((prev) => ({
                ...prev,
                [statementKey]: {...prev[statementKey], [row.node_id]: true},
            }));
        } catch (err) {
            setExpandError(err instanceof Error ? err.message : "Failed to expand row");
        } finally {
            setExpandedLoading((prev) => ({
                ...prev,
                [statementKey]: {...prev[statementKey], [row.node_id]: false},
            }));
        }
    };

    const handleCommitted = async (updated: ModelingWorkbenchData) => {
        setExpanded(EMPTY_EXPANDED);
        setExpandedRows(EMPTY_CHILDREN);
        setExpandedLoading(EMPTY_LOADING);
        setExpandedReconciliation(EMPTY_RECONCILIATION);
        await refresh(updated, {revalidate: false});
    };

    const handleForecastDriverSaved = async () => {
        setExpanded(EMPTY_EXPANDED);
        setExpandedRows(EMPTY_CHILDREN);
        setExpandedLoading(EMPTY_LOADING);
        setExpandedReconciliation(EMPTY_RECONCILIATION);
        await mutate((key: unknown) => typeof key === "string" && key.startsWith(`/api/model-vnext/workspace/${workspaceId}/`));
        await refresh();
    };

    if (isLoading) {
        return (
            <div className="space-y-6 p-4">
                <Skeleton className="h-32 w-full rounded-2xl" />
                <Skeleton className="h-32 w-full rounded-2xl" />
                <Skeleton className="h-72 w-full rounded-2xl" />
                <Skeleton className="h-72 w-full rounded-2xl" />
                <Skeleton className="h-72 w-full rounded-2xl" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-full min-h-[320px] items-center justify-center p-6">
                <div className="max-w-xl rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center shadow-sm">
                    <Loader2Icon className="mx-auto mb-3 size-5 text-slate-400" />
                    <h3 className="text-sm font-semibold text-slate-900">Modeling Workbench awaiting backend support</h3>
                    <p className="mt-2 text-sm text-slate-500">{error}</p>
                </div>
            </div>
        );
    }

    if (!data) return null;

    return (
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto bg-[#F8FAFC] p-4 pb-8">
            <div className="mx-auto w-full max-w-[1680px] space-y-6">
                <header className="rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#1E293B] p-6 shadow-sm" data-tour="workbench-header">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[2px] text-[#93C5FD]">Pecunio | Modeling Workbench</p>
                            <h1 className="mt-2 text-2xl font-semibold text-white">{data.meta.scenario.name}</h1>
                            <p className="mt-1 text-sm text-slate-200">One linked modeling workspace for P&amp;L, Balance Sheet, and Cash Flow.</p>
                            {data.meta.data_freshness && (
                                <div className="mt-2 flex items-center gap-2 text-[10px] text-slate-400">
                                    <span className={`inline-block size-1.5 rounded-full ${
                                        data.meta.data_freshness.status === "fresh" ? "bg-emerald-400" :
                                        data.meta.data_freshness.status === "stale" ? "bg-amber-400" :
                                        data.meta.data_freshness.status === "partial" ? "bg-amber-400" :
                                        "bg-slate-300"
                                    }`} />
                                    <span>
                                        {data.meta.data_freshness.status === "fresh" ? "Data current" :
                                         data.meta.data_freshness.status === "stale" ? "Data may be stale" :
                                         data.meta.data_freshness.status === "partial" ? "Partial sync" :
                                         "Sync status unknown"}
                                    </span>
                                    {data.meta.data_freshness.last_snapshot_sync && (
                                        <span className="text-slate-300">
                                            &middot; Last sync {new Date(data.meta.data_freshness.last_snapshot_sync).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="outline"
                                className="border-[#5BF6C8] text-[#209DB3] hover:bg-[#209DB3]/10"
                                onClick={() => setWorkbenchTourOpen(true)}
                                data-tour="workbench-tour-button"
                            >
                                <CompassIcon className="mr-2 size-4" />
                                Workbench Tour
                            </Button>
                            <Button variant="secondary" onClick={() => setAssumptionsOpen(true)} data-tour="workbench-assumptions-register-button">
                                Scenario Assumptions
                            </Button>
                        </div>
                    </div>
                </header>

                <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm" data-tour="workbench-controls">
                    <div className="flex flex-wrap items-end gap-4">
                        <div className="min-w-[220px] space-y-2">
                            <Label htmlFor="workbench-baseline-method">Baseline Method</Label>
                            <Select value={workbenchBaselineMethod} onValueChange={handleBaselineMethodChange} disabled={isUpdatingBaseline}>
                                <SelectTrigger id="workbench-baseline-method" className="bg-white">
                                    <SelectValue placeholder="Select baseline" />
                                </SelectTrigger>
                                <SelectContent>
                                    {BASELINE_METHOD_OPTIONS.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="min-w-[200px] space-y-2">
                            <Label htmlFor="workbench-forecast-horizon">Forecast Horizon</Label>
                            <Select value={String(workbenchForecastMonths)} onValueChange={(value) => setWorkbenchForecastMonths(Number(value))}>
                                <SelectTrigger id="workbench-forecast-horizon" className="bg-white">
                                    <SelectValue placeholder="Select forecast months" />
                                </SelectTrigger>
                                <SelectContent>
                                    {FORECAST_HORIZON_OPTIONS.map((months) => (
                                        <SelectItem key={months} value={String(months)}>{months} months</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="min-w-[180px] rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-500">Actual Window</p>
                            <p className="mt-1 text-sm font-medium text-slate-900">{formatMonthCount(resolvedActualMonths)}</p>
                        </div>
                        <div className="min-w-[220px] rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[2px] text-slate-500">Current Scenario</p>
                            <p className="mt-1 text-sm font-medium text-slate-900">{data.meta.scenario.name}</p>
                        </div>
                    </div>
                </section>

                <TopKpiStrip kpis={displayKpis} kpiPeriods={data?.kpi_periods} hasScenarioOps={(data?.ops_history ?? []).some((op) => op.is_active)} />
                <RowVisibilityControls options={visibility} onChange={setVisibility} />

                {expandError && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{expandError}</div>
                )}

                <div data-tour="workbench-statements">
                    <ModelingStatementSection
                        workspaceId={workspaceId}
                        scenarioId={scenarioId}
                        months={workbenchForecastMonths}
                        actualMonths={resolvedActualMonths}
                        baselineMethod={workbenchBaselineMethod}
                        statementKey="pnl"
                        statement={data.statements.pnl}
                        rows={data.statements.pnl.rows}
                        isOpen={openSections.pnl}
                        onOpenChange={(open) => setOpenSections((prev) => ({...prev, pnl: open}))}
                        expanded={expanded.pnl}
                        expandedRows={expandedRows.pnl}
                        expandedLoading={expandedLoading.pnl}
                        expandedReconciliation={expandedReconciliation.pnl}
                        visibility={visibility}
                        inlineRevenueGrid={inlineRevenueGrid}
                        inlineCostGrid={inlineCostGrid}
                        inlineRevenueOverrides={revenueOverrideMonths}
                        inlineCostOverrides={costOverrideMonths}
                        opsHistory={data.ops_history}
                        onToggleExpand={handleToggleExpand}
                        onCellSelect={handleCellSelect}
                        onCommitted={handleCommitted}
                        onForecastDriverSaved={handleForecastDriverSaved}
                        onRebuild={() => handleRebuild("pnl")}
                    />
                    <WorkbenchRevenueTieOutSection tieOut={data.revenue_tie_out} />
                    <ModelingStatementSection
                        workspaceId={workspaceId}
                        scenarioId={scenarioId}
                        months={workbenchForecastMonths}
                        actualMonths={resolvedActualMonths}
                        baselineMethod={workbenchBaselineMethod}
                        statementKey="bs"
                        statement={data.statements.bs}
                        rows={data.statements.bs.rows}
                        isOpen={openSections.bs}
                        onOpenChange={(open) => setOpenSections((prev) => ({...prev, bs: open}))}
                        expanded={expanded.bs}
                        expandedRows={expandedRows.bs}
                        expandedLoading={expandedLoading.bs}
                        expandedReconciliation={expandedReconciliation.bs}
                        visibility={visibility}
                        inlineRevenueOverrides={revenueOverrideMonths}
                        inlineCostOverrides={costOverrideMonths}
                        opsHistory={data.ops_history}
                        onToggleExpand={handleToggleExpand}
                        onCellSelect={handleCellSelect}
                        onCommitted={handleCommitted}
                        onForecastDriverSaved={handleForecastDriverSaved}
                        onRebuild={() => handleRebuild("bs")}
                    />
                    <ModelingStatementSection
                        workspaceId={workspaceId}
                        scenarioId={scenarioId}
                        months={workbenchForecastMonths}
                        actualMonths={resolvedActualMonths}
                        baselineMethod={workbenchBaselineMethod}
                        statementKey="cf"
                        statement={data.statements.cf}
                        rows={data.statements.cf.rows}
                        isOpen={openSections.cf}
                        onOpenChange={(open) => setOpenSections((prev) => ({...prev, cf: open}))}
                        expanded={expanded.cf}
                        expandedRows={expandedRows.cf}
                        expandedLoading={expandedLoading.cf}
                        expandedReconciliation={expandedReconciliation.cf}
                        visibility={visibility}
                        inlineRevenueOverrides={revenueOverrideMonths}
                        inlineCostOverrides={costOverrideMonths}
                        opsHistory={data.ops_history}
                        onToggleExpand={handleToggleExpand}
                        onCellSelect={handleCellSelect}
                        onCommitted={handleCommitted}
                        onForecastDriverSaved={handleForecastDriverSaved}
                        onRebuild={() => handleRebuild("cf")}
                    />
                </div>
            </div>

            <AssumptionsRegisterSheet
                open={assumptionsOpen}
                onOpenChange={setAssumptionsOpen}
                opsHistory={data?.ops_history ?? []}
                nodeLabels={nodeLabels}
                scenarioName={data?.meta.scenario?.name ?? "Base"}
                baselineMethod={workbenchBaselineMethod}
                kpis={displayKpis}
            />

            {isPnlActualDrilldownCell(selectedCell) ? (
                <WorkbenchRevenueDrilldownSheet
                    workspaceId={workspaceId}
                    cell={selectedCell}
                    onClose={() => setSelectedCell(null)}
                />
            ) : (
                <WorkbenchCellSheet
                    workspaceId={workspaceId}
                    scenarioId={scenarioId}
                    months={workbenchForecastMonths}
                    actualMonths={actualMonths}
                    baselineMethod={workbenchBaselineMethod}
                    forecastMonthKeys={selectedStatementForecastMonthKeys}
                    cell={selectedCell}
                    opsHistory={data.ops_history}
                    onClose={() => setSelectedCell(null)}
                    onCommitted={handleCommitted}
                    onForecastDriverSaved={handleForecastDriverSaved}
                />
            )}

            <ModelingWorkbenchDetailedTour
                open={workbenchTourOpen}
                activeTab={tourActiveTab}
                onClose={() => setWorkbenchTourOpen(false)}
                onTabChange={handleTourTabChange}
            />
        </div>
    );
}
